// Supported UI locales. Adding a new locale requires creating a new
// dictionary in `./locales/` and registering it in `./index.tsx`.
export type Locale = 'en' | 'id' | 'de' | 'zh-CN' | 'zh-TW' | 'pt-BR' | 'es-ES' | 'ru' | 'fa' | 'ar' | 'ja' | 'ko' | 'pl' | 'hu' | 'fr' | 'uk' | 'tr' | 'th' | 'it';

export const LOCALES: Locale[] = ['en', 'id', 'de', 'zh-CN', 'zh-TW', 'pt-BR', 'es-ES', 'ru', 'fa', 'ar', 'ja', 'ko', 'pl', 'hu', 'fr', 'uk', 'tr', 'th', 'it'];

export const LOCALE_LABEL: Record<Locale, string> = {
  'en': 'English',
  'id': 'Bahasa Indonesia',
  'de': 'Deutsch',
  'zh-CN': '简体中文',
  'zh-TW': '繁體中文',
  'pt-BR': 'Português (Brasil)',
  'es-ES': 'Español (España)',
  'ru': 'Русский',
  'fa': 'فارسی',
  'ar': 'العربية',
  'ja': '日本語',
  'ko': '한국어',
  'pl': 'Polski',
  'hu': 'Magyar',
  'fr': 'Français',
  'uk': 'Українська',
  'tr': 'Türkçe',
  'th': 'ภาษาไทย',
  'it': 'Italiano'
};

// Translation dictionary shape — flat keys, dot-namespaced. We keep it
// flat (not deeply nested) so missing-key TS errors point straight at the
// offending string instead of a generic object mismatch.
export interface Dict {
  // Common
  'common.cancel': string;
  'chat.selectFromLibrary': string;
  'chat.importFigma': string;
  'designFiles.library.label': string;
  'designFiles.library.title': string;
  'libraryPicker.title': string;
  'libraryPicker.searchPlaceholder': string;
  'libraryPicker.empty': string;
  'libraryPicker.allKinds': string;
  'libraryPicker.add': string;
  'libraryPicker.loading': string;
  'common.save': string;
  'common.close': string;
  'common.clear': string;
  'common.delete': string;
  'common.rename': string;
  'common.edit': string;
  'common.preview': string;
  'common.share': string;
  'common.search': string;
  'common.searchEllipsis': string;
  'common.loading': string;
  'common.all': string;
  'common.none': string;
  'common.default': string;
  'common.installed': string;
  'common.notInstalled': string;
  'common.active': string;
  'common.offline': string;
  'common.selected': string;
  'common.create': string;
  'common.openPreview': string;
  'common.exitFullscreen': string;
  'common.fullscreen': string;
  'common.openInNewTab': string;
  'common.exportPdf': string;
  'common.exportZip': string;
  'common.exportHtml': string;
  'common.exportImage': string;
  'common.exportImageFailed': string;
  'common.justNow': string;
  'common.minutesAgo': string;
  'common.hoursAgo': string;
  'common.daysAgo': string;
  'common.weeksAgo': string;
  'common.now': string;
  'common.minutesShort': string;
  'common.hoursShort': string;
  'common.daysShort': string;
  'common.untitled': string;

  // Plugin registry / details
  'plugins.availableDetails.provenance': string;
  'plugins.availableDetails.provenanceLine': string;
  'plugins.availableDetails.provenanceLineWithIntegrity': string;
  'plugins.availableDetails.install': string;
  'plugins.availableDetails.version': string;
  'plugins.availableDetails.pluginVersion': string;
  'plugins.availableDetails.copyInstallCommand': string;
  'plugins.availableDetails.copied': string;
  'plugins.availableDetails.deprecatedPrefix': string;
  'plugins.availableDetails.deprecatedFallback': string;
  'plugins.availableDetails.yanked': string;
  'plugins.availableDetails.yankedWithReason': string;
  'plugins.availableDetails.versionDeprecatedSuffix': string;
  'plugins.availableDetails.versionYankedSuffix': string;
  'plugins.availableDetails.ref': string;
  'plugins.availableDetails.integrity': string;
  'plugins.availableDetails.permissions': string;
  'plugins.availableDetails.capabilitySummary': string;
  'plugins.actions.copyInstallCommand': string;
  'plugins.actions.copyPluginId': string;
  'plugins.actions.copyReadmeBadge': string;
  'plugins.actions.openSourceGithub': string;
  'plugins.actions.openSource': string;
  'plugins.actions.openHomepage': string;
  'plugins.actions.openMarketplace': string;

  // App / brand
  'app.brand': string;
  'app.brandPill': string;
  'app.brandSubtitle': string;
  'app.welcomeLoading': string;

  // Settings dialog
  'settings.welcomeKicker': string;
  'settings.welcomeTitle': string;
  'settings.welcomeSubtitle': string;
  'settings.onboardingCreateTitle': string;
  'settings.onboardingCreateBody': string;
  'settings.onboardingMemoryTitle': string;
  'settings.onboardingMemoryBody': string;
  // Onboarding step 2 — memory callout (answers seed the profile)
  'settings.onboardingMemoryCalloutTitle': string;
  'settings.onboardingMemoryCalloutBody': string;
  'settings.onboardingMemoryBenefitIntent': string;
  'settings.onboardingMemoryBenefitFewerQuestions': string;
  'settings.onboardingMemoryBenefitPersonalized': string;
  'settings.onboardingSystemsTitle': string;
  'settings.onboardingSystemsBody': string;
  'settings.onboardingExecutionTitle': string;
  'settings.onboardingExecutionBody': string;
  'settings.onboardingAmrCloudBenefitOfficial': string;
  'settings.onboardingAmrCloudBenefitReady': string;
  'settings.onboardingAmrCloudBenefitModels': string;
  'settings.onboardingAmrCloudBenefitPricing': string;
  'settings.onboardingAmrCloudUpcomingLabel': string;
  'settings.onboardingAmrCloudUpcomingImageVideo': string;
  'settings.onboardingAmrCloudUpcomingSkills': string;
  'settings.onboardingAmrCloudUpcomingRouting': string;
  'settings.onboardingAmrModelSourceLabel': string;
  'settings.onboardingAmrCloudAuthorizeAction': string;
  'settings.onboardingAmrCloudAuthorizedAction': string;
  'settings.onboardingStepConnect': string;
  'settings.onboardingStepDesignSystem': string;
  'settings.onboardingStepProfile': string;
  'settings.onboardingStepNewsletter': string;
  'settings.onboardingNewsletterTitle': string;
  'settings.onboardingNewsletterBody': string;
  'settings.onboardingConnectTitle': string;
  'settings.onboardingConnectBody': string;
  'settings.onboardingCloudTitle': string;
  'settings.onboardingCloudBody': string;
  'settings.onboardingCloudSignIn': string;
  'settings.onboardingCloudContinue': string;
  'settings.onboardingCloudAlternative': string;
  'settings.onboardingCloudOr': string;
  'settings.onboardingCloudRights': string;
  'settings.onboardingGateTooltipNoRuntime': string;
  'settings.onboardingGateTooltipAmr': string;
  'settings.onboardingGateTooltipLocal': string;
  'settings.onboardingGateTooltipByok': string;
  'settings.onboardingRecommended': string;
  'settings.onboardingAmrCloudOfficialBadge': string;
  'settings.onboardingLocalTitle': string;
  'settings.onboardingLocalBody': string;
  'settings.onboardingLocalAction': string;
  'settings.onboardingCliScanHint': string;
  'settings.onboardingByokTitle': string;
  'settings.onboardingByokBody': string;
  'settings.onboardingByokAction': string;
  'settings.onboardingDesignTitle': string;
  'settings.onboardingDesignBody': string;
  'settings.onboardingDesignIntroGenerateTitle': string;
  'settings.onboardingDesignIntroGenerateBody': string;
  'settings.onboardingDesignIntroReuseTitle': string;
  'settings.onboardingDesignIntroReuseBody': string;
  'settings.onboardingDesignIntroSkipTitle': string;
  'settings.onboardingDesignIntroSkipBody': string;
  'settings.onboardingGithubTitle': string;
  'settings.onboardingGithubBody': string;
  'settings.onboardingUploadTitle': string;
  'settings.onboardingUploadBody': string;
  'settings.onboardingPromptTitle': string;
  'settings.onboardingPromptBody': string;
  'settings.onboardingProfileTitle': string;
  'settings.onboardingProfileBody': string;
  'settings.onboardingRoleLabel': string;
  'settings.onboardingOrgSizeLabel': string;
  'settings.onboardingUseCaseLabel': string;
  'settings.onboardingSourceLabel': string;
  'settings.onboardingSelectPlaceholder': string;
  'settings.onboardingSelectMultiplePlaceholder': string;
  'settings.onboardingOrgSolo': string;
  'settings.onboardingOrgTeam': string;
  'settings.onboardingOrgStartup': string;
  'settings.onboardingOrgGrowth': string;
  'settings.onboardingOrgMidMarket': string;
  'settings.onboardingOrgEnterprise': string;
  'settings.onboardingRolePm': string;
  'settings.onboardingRoleDesigner': string;
  'settings.onboardingRoleEngineer': string;
  'settings.onboardingRoleMarketing': string;
  'settings.onboardingRoleAgency': string;
  'settings.onboardingRoleGrowth': string;
  'settings.onboardingRoleOps': string;
  'settings.onboardingRoleFounder': string;
  'settings.onboardingRoleStudent': string;
  'settings.onboardingRoleOther': string;
  'settings.onboardingUseProduct': string;
  'settings.onboardingUseDesignSystem': string;
  'settings.onboardingUsePrototype': string;
  'settings.onboardingUseLanding': string;
  'settings.onboardingUseMarketing': string;
  'settings.onboardingUseAds': string;
  'settings.onboardingUseDashboard': string;
  'settings.onboardingUseDeck': string;
  'settings.onboardingUseEngineering': string;
  'settings.onboardingUseAgency': string;
  'settings.onboardingSourceGithub': string;
  'settings.onboardingSourceFriend': string;
  'settings.onboardingSourceSocial': string;
  'settings.onboardingSourceProductHunt': string;
  'settings.onboardingSourceCommunity': string;
  'settings.onboardingSourceYoutube': string;
  'settings.onboardingSourceBlog': string;
  'settings.onboardingSourceAiTool': string;
  'settings.onboardingSourceSearch': string;
  'settings.onboardingSourceEvent': string;
  'settings.onboardingBack': string;
  'settings.onboardingContinue': string;
  'settings.onboardingFinish': string;
  'settings.kicker': string;
  'settings.title': string;
  'settings.subtitle': string;
  'settings.modeAria': string;
  'settings.protocolAria': string;
  'settings.modeDaemon': string;
  'settings.modeDaemonHelp': string;
  'settings.modeDaemonOffline': string;
  'settings.modeDaemonOfflineMeta': string;
  'settings.modeDaemonInstalledMeta': string;
  'settings.modeApi': string;
  'settings.modeApiMeta': string;
  'settings.byokNoFileToolsNotice': string;
  'settings.codeAgent': string;
  'settings.codeAgentHint': string;
  'settings.rescan': string;
  'settings.rescanTitle': string;
  'settings.rescanRunning': string;
  'settings.rescanSuccess': string;
  'settings.rescanFailed': string;
  'settings.test': string;
  'settings.testTitle': string;
  'settings.testRunning': string;
  'settings.byokReadyToTest': string;
  'settings.testCancel': string;
  'settings.testRetry': string;
  'settings.required': string;
  'settings.testMissingFields': string;
  'settings.testSuccessApi': string;
  'settings.testSuccessCli': string;
  'settings.testAuthFailed': string;
  'settings.testForbidden': string;
  'settings.testNotFoundModel': string;
  'settings.testInvalidModelId': string;
  'settings.testInvalidBaseUrl': string;
  'settings.testRateLimited': string;
  'settings.testUpstream': string;
  'settings.testTimeout': string;
  'settings.testAgentMissing': string;
  'settings.testAgentSpawn': string;
  'settings.testUnknown': string;
  'settings.agentInstall.install': string;
  'settings.agentInstall.docs': string;
  'settings.agentInstall.pathHint': string;
  'settings.agentInstall.stepOpenLinks': string;
  'settings.agentInstall.stepAuth': string;
  'settings.agentInstall.stepRescan': string;
  'settings.agentInstall.stepSelect': string;
  'settings.noAgentsDetected': string;
  'settings.agentInstalledGroup': string;
  'settings.agentInstallGroup': string;
  'settings.agentAuthRequired': string;
  'settings.agentAuthUnknown': string;
  'settings.advanced': string;
  'settings.amrLogin': string;
  'settings.amrLogout': string;
  'settings.amrLoggingIn': string;
  'settings.amrLoggingOut': string;
  'settings.amrLoggedInAs': string;
  'settings.amrLoggedInWithPlan': string;
  'settings.amrLoggedInPill': string;
  'settings.amrNotLoggedIn': string;
  'settings.amrCloud': string;
  'settings.amrAuthorize': string;
  'settings.amrBenefitOfficial': string;
  'settings.amrBenefitLowerPrice': string;
  'settings.amrBenefitManyModels': string;
  'settings.amrPromoBonus': string;
  'settings.amrSignInToContinue': string;
  'settings.amrSignIn': string;
  'settings.amrSignedIn': string;
  'settings.amrWalletBalance': string;
  'settings.amrWalletUnavailable': string;
  'settings.amrWalletUpdatedAt': string;
  'settings.amrWalletCached': string;
  'settings.amrWalletRefresh': string;
  'settings.amrWalletRefreshTitle': string;
  'settings.amrNotSignedIn': string;
  'settings.amrSigningIn': string;
  'settings.amrActivationHint': string;
  'settings.amrActivationBrowserFailed': string;
  'settings.amrActivationOpen': string;
  'settings.amrCancelSignIn': string;
  'settings.amrAccountStatus': string;
  'settings.amrConsole': string;
  'settings.amrLoginErrorCompact': string;
  'settings.apiSection': string;
  'settings.quickFillProvider': string;
  'settings.providerPreset': string;
  'settings.protocolGroupProtocols': string;
  'settings.protocolGroupGateways': string;
  'settings.customProvider': string;
  'settings.apiKey': string;
  'settings.apiKeyGetLink': string;
  'settings.showKey': string;
  'settings.hideKey': string;
  'settings.show': string;
  'settings.hide': string;
  'settings.model': string;
  'settings.apiKeyInvalid': string;
  'settings.apiKeyCleaned': string;
  'settings.modelsLoadedFromAccount': string;
  'settings.modelsLoadedCount': string;
  'settings.modelSourceAccount': string;
  'settings.modelSourceSuggested': string;
  'settings.fetchModels': string;
  'settings.fetchModelsTitle': string;
  'settings.fetchModelsRunning': string;
  'settings.fetchModelsMissingFields': string;
  'settings.fetchModelsInvalidBaseUrl': string;
  'settings.fetchModelsUnsupportedAzure': string;
  'settings.fetchModelsUnsupportedOllama': string;
  'settings.fetchModelsSuccess': string;
  'settings.fetchModelsEmpty': string;
  'settings.fetchModelsUnsupported': string;
  'settings.fetchModelsFailed': string;
  'settings.suggestedModelsHint': string;
  'settings.maxTokens': string;
  'settings.maxTokensHint': string;
  'settings.baseUrl': string;
  'settings.baseUrlInvalid': string;
  'settings.baseUrlCustomize': string;
  'settings.baseUrlDefaultHint': string;
  'settings.azureBaseUrlPlaceholder': string;
  'settings.azureBaseUrlHint': string;
  'settings.azureDeploymentModel': string;
  'settings.azureCustomDeploymentName': string;
  'settings.azureDeploymentModelHint': string;
  'settings.azureModelFetchHint': string;
  'settings.apiVersion': string;
  'settings.byokImageModel': string;
  'settings.byokVideoModel': string;
  'settings.byokVideoI2vHint': string;
  'settings.byokSpeechModel': string;
  'settings.byokSpeechVoice': string;
  'settings.byokModelDefaultOption': string;
  'settings.apiHint': string;
  'settings.skipForNow': string;
  'settings.getStarted': string;
  'settings.envConfigure': string;
  'settings.localCli': string;
  'settings.anthropicApi': string;
  'settings.noAgentSelected': string;
  'settings.language': string;
  'settings.languageHint': string;
  'settings.appearance': string;
  'settings.appearanceHint': string;
  'settings.themeSystem': string;
  'settings.themeLight': string;
  'settings.themeDark': string;
  'settings.agentModelHead': string;
  'settings.modelPicker': string;
  'settings.modelSourceLive': string;
  'settings.modelSourceFallback': string;
  'settings.reasoningPicker': string;
  'settings.modelPickerHint': string;
  'settings.modelPickerLiveHint': string;
  'settings.modelPickerLiveCatalogOnlyHint': string;
  'settings.modelPickerFallbackHint': string;
  'settings.cliEnvTitle': string;
  'settings.cliEnvHint': string;
  'settings.cliEnvClaudeConfigDir': string;
  'settings.cliEnvClaudeBaseUrl': string;
  'settings.cliEnvClaudeApiKey': string;
  'settings.cliEnvCodexHome': string;
  'settings.cliEnvCodexBin': string;
  'settings.cliEnvCodexBaseUrl': string;
  'settings.cliEnvCodexApiKey': string;
  'settings.modelCustom': string;
  'settings.modelCustomLabel': string;
  'settings.modelCustomPlaceholder': string;
  'settings.mediaProviders': string;
  'settings.mediaProvidersHint': string;
  'settings.mcpServerTitle': string;
  'settings.mcpServerHint': string;
  'settings.externalMcpTitle': string;
  'settings.externalMcpHint': string;
  'settings.mediaProviderApiKey': string;
  'settings.mediaProviderBaseUrl': string;
  'settings.mediaProviderConfigured': string;
  'settings.mediaProviderUnset': string;
  'settings.mediaProviderClear': string;
  'settings.mediaProviderClearConfirm': string;
  'settings.mediaProviderPlaceholder': string;
  'settings.mediaProviderBaseUrlPlaceholder': string;
  'settings.mediaProviderReload': string;
  'settings.mediaProviderReloadError': string;
  'settings.mediaProviderReloadSuccess': string;
  'settings.mediaProviderLoadError': string;
  'settings.mediaProviderComingSoonHint': string;
  'settings.privacy': string;
  'settings.privacyHint': string;
  'settings.privacyConsentKicker': string;
  'settings.privacyConsentLead': string;
  'settings.privacyConsentFooter': string;
  'settings.privacyConsentShare': string;
  'settings.privacyConsentDecline': string;
  'settings.privacyConsentAccept': string;
  'settings.privacyConsentBannerFooter': string;
  'settings.privacyConsentPolicyLink': string;
  'settings.privacyMetrics': string;
  'settings.privacyMetricsHint': string;
  'settings.privacyContent': string;
  'settings.privacyContentHint': string;
  'settings.privacyInstallationId': string;
  'settings.privacyOptedOut': string;
  'settings.privacyDataDeletion': string;
  'settings.privacyDataDeletionHint': string;
  'settings.about': string;
  'settings.aboutHint': string;
  'settings.appVersion': string;
  'settings.appChannel': string;
  'settings.appRuntime': string;
  'settings.appPlatform': string;
  'settings.appArchitecture': string;
  'settings.runtimePackaged': string;
  'settings.runtimeDevelopment': string;
  'settings.versionUnavailable': string;
  'settings.installLatest': string;
  'settings.alreadyLatest': string;
  'settings.skills': string;
  'settings.skillsHint': string;
  'settings.skillsNew': string;
  'settings.skillsEmpty': string;
  'settings.skillsEdit': string;
  'settings.skillsDelete': string;
  'settings.skillsDeleteConfirm': string;
  'settings.skillsName': string;
  'settings.skillsTriggers': string;
  'settings.skillsDescription': string;
  'settings.skillsBody': string;
  'settings.skillsCreate': string;
  'settings.skillsSave': string;
  'settings.skillsSaving': string;
  'settings.skillsFiles': string;
  'settings.skillsNoFiles': string;
  'settings.skillsNameRequired': string;
  'settings.skillsBodyRequired': string;
  'settings.designSystems': string;
  'settings.designSystemsHint': string;
  'settings.designSystemsInstalled': string;
  'settings.designSystemRenameFailed': string;
  'settings.designSystemsAdd': string;
  'settings.designSystemsHiddenCount': string;
  'settings.designSystemsShowAll': string;
  'settings.designSystemsShowHidden': string;
  'settings.designSystemsSource': string;
  'settings.designSystemsSourceLocal': string;
  'settings.designSystemsSourceGithub': string;
  'settings.designSystemsSourceShadcn': string;
  'settings.designSystemsShadcnReference': string;
  'settings.designSystemsImportShadcn': string;
  'settings.designSystemsStructure': string;
  'settings.designSystemsModeHybrid': string;
  'settings.designSystemsModeNormalized': string;
  'settings.designSystemsModeVerbatim': string;
  'settings.designSystemsCraft': string;
  'settings.designSystemsCraftColor': string;
  'settings.designSystemsCraftAccessibility': string;
  'settings.designSystemsGithubUrl': string;
  'settings.designSystemsProjectPath': string;
  'settings.designSystemsImportGithub': string;
  'settings.designSystemsImportProject': string;
  'settings.designSystemsImportedStatus': string;
  'settings.designSystemsViewImported': string;
  'settings.designSystemsCategory': string;
  'settings.designSystemsAllCategories': string;
  'settings.designSystemsShowInHomeGallery': string;
  'settings.projectLocations': string;
  'settings.projectLocationsHint': string;
  'settings.projectLocationsDescription': string;
  'settings.projectLocationsSaveError': string;
  'settings.projectLocationsSaved': string;
  'settings.projectLocationsScanError': string;
  'settings.projectLocationsScanComplete': string;
  'settings.projectLocationsNoFolderSelected': string;
  'settings.projectLocationsDuplicate': string;
  'settings.projectLocationsWorkBaseMeta': string;
  'settings.projectLocationsAddFolder': string;
  'settings.projectLocationsDefaultBadge': string;
  'settings.projectLocationsMakeDefault': string;
  'settings.projectLocationsDefaultSaved': string;
  'settings.librarySkills': string;
  'settings.libraryDesignSystems': string;
  'settings.librarySearch': string;
  'settings.libraryAll': string;
  'settings.libraryPreview': string;
  'settings.libraryPreviewClose': string;
  'settings.libraryLoading': string;
  'settings.libraryNoResults': string;
  'settings.libraryEnabled': string;
  'settings.libraryDisabled': string;
  'settings.libraryToggleLabel': string;
  'settings.libraryInstall': string;
  'settings.libraryInstallGithub': string;
  'settings.libraryInstallLocal': string;
  'settings.libraryInstallUrl': string;
  'settings.libraryInstallPath': string;
  'settings.libraryInstallButton': string;
  'settings.libraryUninstall': string;
  'settings.libraryBuiltIn': string;
  'settings.libraryInstalled': string;
  'settings.connectorsNavHint': string;
  'settings.connectorsHint': string;
  'settings.connectorsComposioApiKey': string;
  'settings.connectorsSavedTitle': string;
  'settings.connectorsSavedWithTail': string;
  'settings.connectorsSaved': string;
  'settings.connectorsGetApiKey': string;
  'settings.connectorsReplaceKeyPlaceholder': string;
  'settings.connectorsApiKeyPlaceholder': string;
  'settings.connectorsClear': string;
  'settings.connectorsClearConfirmTitle': string;
  'settings.connectorsClearConfirmBody': string;
  'settings.connectorsClearConfirmContinue': string;
  'settings.connectorsClearFinalTitle': string;
  'settings.connectorsClearFinalBody': string;
  'settings.connectorsClearFinalConfirm': string;
  'settings.connectorsClearArming': string;
  'settings.connectorsClearCancel': string;
  'settings.connectorsSaveKey': string;
  'settings.connectorsSaveKeyTitle': string;
  'settings.connectorsKeySaving': string;
  'settings.connectorsKeySaved': string;
  'settings.connectorsKeyError': string;
  'settings.connectorsHelpSaved': string;
  'settings.connectorsHelpUnsaved': string;
  'settings.connectorsHelpEmpty': string;
  'settings.connectorsLoadingSavedKey': string;
  'settings.autosaveSaving': string;
  'settings.autosaveSaved': string;
  'settings.autosaveError': string;
  'settings.orbit.eyebrow': string;
  'settings.orbit.title': string;
  'settings.orbit.navHint': string;
  'settings.orbit.lede': string;
  'settings.orbit.statusOnTitle': string;
  'settings.orbit.statusOffTitle': string;
  'settings.orbit.statusActive': string;
  'settings.orbit.statusOff': string;
  'settings.orbit.runTitle': string;
  'settings.orbit.running': string;
  'settings.orbit.runOpen': string;
  'settings.orbit.dailySummaryTitle': string;
  'settings.orbit.dailySummarySub': string;
  'settings.orbit.on': string;
  'settings.orbit.off': string;
  'settings.orbit.runTimeTitle': string;
  'settings.orbit.runTimeSub': string;
  'settings.orbit.runTimeAria': string;
  'settings.orbit.nextRun': string;
  'settings.orbit.nextRunScheduledAfterSave': string;
  'settings.orbit.schedule': string;
  'settings.orbit.pausedManualOnly': string;
  'settings.orbit.templateTitle': string;
  'settings.orbit.templateMissing': string;
  'settings.orbit.templateMissingOption': string;
  'settings.orbit.templateMissingInstall': string;
  'settings.orbit.templateMissingPickAnother': string;
  'settings.orbit.templateResetTitle': string;
  'settings.orbit.templateReset': string;
  'settings.orbit.templateHelp': string;
  'settings.orbit.templateAria': string;
  'settings.orbit.templatesLoading': string;
  'settings.orbit.templatesOptgroup': string;
  'settings.orbit.lastRun': string;
  'settings.orbit.triggerManual': string;
  'settings.orbit.triggerScheduled': string;
  'settings.orbit.meterAria': string;
  'settings.orbit.countChecked': string;
  'settings.orbit.countSucceeded': string;
  'settings.orbit.countSkipped': string;
  'settings.orbit.countFailed': string;
  'settings.orbit.runError': string;
  'settings.orbit.artifactKickerLive': string;
  'settings.orbit.artifactKickerLegacy': string;
  'settings.orbit.artifactTitle': string;
  'settings.orbit.artifactMetaLive': string;
  'settings.orbit.artifactMetaLegacy': string;
  'settings.orbit.copyMarkdownTitle': string;
  'settings.orbit.copied': string;
  'settings.orbit.copy': string;
  'settings.orbit.openArtifact': string;
  'settings.orbit.sourceMarkdown': string;
  'settings.orbit.gateAriaLabel': string;
  'settings.orbit.gateEyebrow': string;
  'settings.orbit.gateTitle': string;
  'settings.orbit.gateBody': string;
  'settings.orbit.gateBodyNoKey': string;
  'settings.orbit.gateAction': string;
  'settings.orbit.gateActionNoKey': string;
  'settings.orbit.gateLoading': string;
  'settings.orbit.controlsLockedBadge': string;
  'settings.orbit.controlsLockedHint': string;

  // Memory (auto-extracted personalization saved as on-disk markdown)
  'settings.memory': string;
  'settings.memoryHint': string;
  'settings.customInstructionsTitle': string;
  'settings.customInstructionsHint': string;
  'settings.customInstructionsPlaceholder': string;
  'settings.memoryDescription': string;
  'settings.memoryEnabled': string;
  'settings.memoryDisabled': string;
  'settings.memoryEnableLabel': string;
  'settings.memoryDisabledBanner': string;
  'settings.memoryNew': string;
  'settings.memoryEdit': string;
  'settings.memoryDelete': string;
  'settings.memoryPreview': string;
  'settings.memoryEmpty': string;
  'settings.memoryEmptyHintZh': string;
  'settings.memoryEmptyHintEn': string;
  'settings.memoryName': string;
  'settings.memoryDesc': string;
  'settings.memoryBody': string;
  'settings.memoryBodyHint': string;
  'settings.memoryStartersLabel': string;
  'settings.memoryStarterUserName': string;
  'settings.memoryStarterUserDesc': string;
  'settings.memoryStarterUserBody': string;
  'settings.memoryStarterFeedbackName': string;
  'settings.memoryStarterFeedbackDesc': string;
  'settings.memoryStarterFeedbackBody': string;
  'settings.memoryStarterProjectName': string;
  'settings.memoryStarterProjectDesc': string;
  'settings.memoryStarterProjectBody': string;
  'settings.memorySaveHint': string;
  'settings.memoryIndexSaveHint': string;
  'settings.memoryIndexUnsaved': string;
  'settings.memoryFlashCreated': string;
  'settings.memoryFlashSaved': string;
  'settings.memoryFlashDeleted': string;
  'settings.memoryFlashIndexSaved': string;
  'settings.memoryFlashPathCopied': string;
  'settings.memoryNameLabel': string;
  'settings.memoryTypeLabel': string;
  'settings.memoryDescLabel': string;
  'settings.memoryBodyLabel': string;
  'settings.memoryTypeUser': string;
  'settings.memoryTypeFeedback': string;
  'settings.memoryTypeProject': string;
  'settings.memoryTypeReference': string;
  'settings.memoryTypeProfile': string;
  'settings.memoryTypeRule': string;
  // Memory → top-level IA tabs (Memories vs. How it works)
  'settings.memoryTabMemories': string;
  'settings.memoryTabMemoriesCaption': string;
  'settings.memoryTabHow': string;
  'settings.memoryTabHowCaption': string;
  'settings.memoryAddDisclosure': string;
  'settings.memoryAddDisclosureHint': string;
  // Memory → two-loop: structured profile editor
  'settings.memoryProfileTab': string;
  'settings.memoryProfileTabCaption': string;
  'settings.memoryProfileTitle': string;
  'settings.memoryProfileHint': string;
  'settings.memoryProfileName': string;
  'settings.memoryProfileDescription': string;
  'settings.memoryProfileSave': string;
  'settings.memoryProfileSaving': string;
  'settings.memoryProfileSaved': string;
  'settings.memoryProfileRole': string;
  'settings.memoryProfileRolePlaceholder': string;
  'settings.memoryProfileCompany': string;
  'settings.memoryProfileCompanyPlaceholder': string;
  'settings.memoryProfileDomain': string;
  'settings.memoryProfileDomainPlaceholder': string;
  'settings.memoryProfileAudience': string;
  'settings.memoryProfileAudiencePlaceholder': string;
  'settings.memoryProfileAesthetic': string;
  'settings.memoryProfileAestheticPlaceholder': string;
  'settings.memoryProfileDeliverables': string;
  'settings.memoryProfileDeliverablesPlaceholder': string;
  'settings.memoryProfileLocale': string;
  'settings.memoryProfileLocalePlaceholder': string;
  'settings.memoryProfileGoals': string;
  'settings.memoryProfileGoalsPlaceholder': string;
  // Memory → two-loop: pluggable hooks panel
  'settings.memoryHooksTitle': string;
  'settings.memoryHooksHint': string;
  'settings.memoryHooksProfileLabel': string;
  'settings.memoryHooksProfileDesc': string;
  'settings.memoryHooksRewriteLabel': string;
  'settings.memoryHooksRewriteDesc': string;
  'settings.memoryHooksVerifyLabel': string;
  'settings.memoryHooksVerifyDesc': string;
  'settings.memoryHooksExtractionLabel': string;
  'settings.memoryHooksExtractionDesc': string;
  'settings.memoryIndex': string;
  'settings.memoryIndexSave': string;
  'settings.memoryIndexReset': string;
  'settings.memoryToastChanged': string;
  'settings.memoryToastClickHint': string;
  'settings.memoryAll': string;
  // Memory → LLM extraction observability
  'settings.memoryExtractions': string;
  'settings.memoryExtractionsHint': string;
  'settings.memoryExtractionsEmpty': string;
  'settings.memoryExtractionsRefresh': string;
  'settings.memoryExtractionsRefreshing': string;
  'settings.memoryExtractionPhaseRunning': string;
  'settings.memoryExtractionPhaseSuccess': string;
  'settings.memoryExtractionPhaseSkipped': string;
  'settings.memoryExtractionPhaseFailed': string;
  'settings.memoryExtractionSkipNoProvider': string;
  'settings.memoryExtractionSkipDisabled': string;
  'settings.memoryExtractionSkipEmpty': string;
  'settings.memoryExtractionSkipNoMatch': string;
  'settings.memoryExtractionKindHeuristic': string;
  'settings.memoryExtractionKindLlm': string;
  'settings.memoryExtractionProviderEnv': string;
  'settings.memoryExtractionProviderMediaConfig': string;
  'settings.memoryExtractionProviderOverride': string;
  'settings.memoryExtractionProposed': string;
  'settings.memoryExtractionWritten': string;
  'settings.memoryExtractionDuration': string;
  'settings.memoryExtractionDelete': string;
  'settings.memoryExtractionsClear': string;
  'settings.memoryExtractionsClearTitle': string;
  'settings.memoryExtractionsClearConfirm': string;
  'settings.memoryNoProviderBannerTitle': string;
  'settings.memoryNoProviderBannerBody': string;
  // Memory model picker — rendered inline next to the chat model picker
  // so picking "the fast model that mines facts each turn" lives in the
  // same row as the chat agent + model. Reuses the surrounding chat
  // protocol/CLI context (key, baseUrl, apiVersion); the user only
  // chooses the model id.
  'settings.memoryModelInlineLabel': string;
  'settings.memoryModelInlineSameAsChat': string;
  'settings.memoryModelInlineSameAsChatWithModel': string;
  'settings.memoryModelInlineSameAsChatWithProvider': string;
  'settings.memoryModelInlineHintCli': string;
  'settings.memoryModelInlineHintCliConstrained': string;
  'settings.memoryModelInlineHintByok': string;
  'settings.memoryModelInlineHintByokNeutral': string;
  'settings.memoryModelInlineFlashSaved': string;
  'settings.memoryModelInlineFlashCleared': string;

  // MCP server settings
  'settings.mcpTitle': string;
  'settings.mcpHint': string;
  'settings.mcpDaemonError': string;
  'settings.mcpBuildDaemon': string;
  'settings.mcpNodeMissing': string;
  'settings.mcpBuildHint': string;
  'settings.mcpMethodCli': string;
  'settings.mcpInstructionCli': string;
  'settings.mcpMethodToml': string;
  'settings.mcpInstructionCodex': string;
  'settings.mcpMethodOneClick': string;
  'settings.mcpInstructionCursor': string;
  'settings.mcpDeeplinkInstallCursor': string;
  'settings.mcpMethodJson': string;
  'settings.mcpInstructionCopilot': string;
  'settings.mcpInstructionAntigravity': string;
  'settings.mcpInstructionZed': string;
  'settings.mcpInstructionWindsurf': string;
  'settings.mcpCopyAria': string;
  'settings.mcpResolvingFailed': string;
  'settings.mcpLoadingPaths': string;
  'settings.mcpCopied': string;
  'settings.mcpCopy': string;
  'settings.mcpCursorApproval': string;
  'settings.mcpCodexOneClickInstall': string;
  'settings.mcpCodexOneClickUninstall': string;
  'settings.mcpCodexOneClickUnavailable': string;
  'settings.mcpCodexInstallSuccess': string;
  'settings.mcpCodexUninstallSuccess': string;
  'settings.mcpCodexBusy': string;
  'settings.mcpCodexInstallError': string;
  'settings.mcpRestartNote': string;
  'settings.mcpRestartDetail': string;
  'settings.mcpCapabilitiesTitle': string;
  'settings.mcpCapabilityRead': string;
  'settings.mcpCapabilityPull': string;
  'settings.mcpCapabilityDefault': string;
  'settings.mcpRunningNote': string;

  // Notifications (settings + system notifications)
  'settings.notifications': string;
  'settings.notificationsHint': string;
  'settings.notifyCompletionSound': string;
  'settings.notifyCompletionSoundHint': string;
  'settings.notifySuccessSound': string;
  'settings.notifyFailureSound': string;
  'settings.notifyDesktop': string;
  'settings.notifyDesktopHint': string;
  'settings.notifyDesktopBlocked': string;
  'settings.notifyDesktopUnsupported': string;
  'settings.notifyTest': string;
  'settings.notifyTestSent': string;
  'settings.notifyTestFailed': string;
  'settings.notifySoundDing': string;
  'settings.notifySoundChime': string;
  'settings.notifySoundTwoToneUp': string;
  'settings.notifySoundPluck': string;
  'settings.notifySoundBuzz': string;
  'settings.notifySoundTwoToneDown': string;
  'settings.notifySoundThud': string;
  'notify.successTitle': string;
  'notify.failureTitle': string;
  'notify.successBody': string;
  'notify.failureBody': string;

  // Desktop updater
  'updater.available': string;
  'updater.availableBody': string;
  'updater.checking': string;
  'updater.download': string;
  'updater.downloading': string;
  'updater.downloadingPercent': string;
  'updater.done': string;
  'updater.failed': string;
  'updater.installerOpenBody': string;
  'updater.installerOpened': string;
  'updater.later': string;
  'updater.openFailedFallback': string;
  'updater.installRestart': string;
  'updater.installingRestart': string;
  'updater.openInstaller': string;
  'updater.opening': string;
  'updater.payloadReadyGeneric': string;
  'updater.payloadReadyVersion': string;
  'updater.quitButton': string;
  'updater.quitFailedBody': string;
  'updater.quitFailedTitle': string;
  'updater.quitting': string;
  'updater.ready': string;
  'updater.readyGeneric': string;
  'updater.readyVersion': string;
  'updater.upToDate': string;

  // Newsletter email field on the onboarding About-you step.
  'newsletter.label': string;
  'newsletter.placeholder': string;

  // Entry view / tabs
  'entry.tabDesigns': string;
  'entry.tabTemplates': string;
  'entry.tabDesignSystems': string;
  'entry.tabConnectors': string;
  'entry.tabImageTemplates': string;
  'entry.tabVideoTemplates': string;
  'entry.openSettingsTitle': string;
  'entry.openSettingsAria': string;
  'entry.discordLabel': string;
  'entry.discordAria': string;
  'entry.discordAriaWithOnline': string;
  'entry.discordOnlineLabel': string;
  'entry.followXLabel': string;
  'entry.followThreadsLabel': string;
  'entry.youtubeLabel': string;
  'entry.resizeAria': string;
  'entry.loadingWorkspace': string;
  'entry.useEverywhereTitle': string;
  'entry.useEverywhereAria': string;
  'entry.workspaceTeamsLabel': string;
  'entry.workspaceTeamsTitle': string;
  'entry.workspaceTeamsAria': string;
  // Left nav rail (icon-only) — surface labels also serve as tooltips
  'entry.navExpand': string;
  'entry.navCollapse': string;
  'entry.navNewProject': string;
  'entry.navHome': string;
  'entry.navProjects': string;
  'entry.navTasks': string;
  'entry.navPlugins': string;
  'entry.navDesignSystems': string;
  'entry.navBrands': string;
  'entry.navIntegrations': string;
  'workspaceTabs.project': string;
  'workspaceTabs.pluginDetails': string;
  'workspaceTabs.marketplace': string;

  // Entry home
  'homeHero.title': string;
  'homeHero.startWithTemplate': string;
  'homeHero.startBlankProject': string;
  'homeHero.templatePicker.label': string;
  'homeHero.templatePicker.searchPlaceholder': string;
  'homeHero.templatePicker.projectTypes': string;
  'homeHero.templatesScrollHint': string;
  'homeHero.templatesCollapse': string;
  'homeHero.subtitlePrefix': string;
  'homeHero.placeholder': string;
  'homeHero.placeholderActive': string;
  // Rotating typewriter placeholder scenarios + idle hint (home-hero carousel).
  'homeHero.carousel.hint': string;
  'homeHero.carousel.onePageBrief': string;
  'homeHero.carousel.notesToDeck': string;
  'homeHero.carousel.signupFlow': string;
  'homeHero.carousel.improveBrief': string;
  'homeHero.carousel.loadingAnimation': string;
  'homeHero.carousel.teamUpdateSlides': string;
  'homeHero.carousel.ordersDashboard': string;
  'homeHero.carousel.productDetail': string;
  'homeHero.carousel.caseStudy': string;
  'homeHero.carousel.landingIntro': string;
  'homeHero.carousel.pitchDeck': string;
  'homeHero.carousel.appIdea': string;
  'homeHero.carousel.landingLayout': string;
  'homeHero.skills': string;
  'homeHero.addMenu': string;
  'homeHero.addPlugin': string;
  'homeHero.addConnectors': string;
  'homeHero.addMcp': string;
  'homeHero.noPlugins': string;
  'homeHero.noMcp': string;
  'homeHero.noConnectors': string;
  'homeHero.applying': string;
  'homeHero.pluginTitle': string;
  'homeHero.pluginPrefix': string;
  'homeHero.skillPrefix': string;
  'homeHero.removePlugin': string;
  'homeHero.removePluginAria': string;
  'homeHero.clearActivePlugin': string;
  'homeHero.clearActiveSkill': string;
  'homeHero.contextItemsResolved': string;
  'homeHero.removeFile': string;
  'homeHero.contextSearchResults': string;
  'homeHero.contextSurfaces': string;
  'homeHero.loadingContext': string;
  'homeHero.noResults': string;
  'homeHero.searchPrompt': string;
  'homeHero.parameters': string;
  'homeHero.details': string;
  'homeHero.toRun': string;
  'homeHero.forNewLine': string;
  'homeHero.run': string;
  'homeHero.typeSomethingToRun': string;
  'homeHero.promptExamples': string;
  'homeHero.footer.designSystem': string;
  'homeHero.footer.autoDesignSystem': string;
  'homeHero.footer.autoDesignSystemSummary': string;
  'homeHero.footer.ratio': string;
  'homeHero.footer.duration': string;
  'homeHero.footer.resolution': string;
  'homeHero.footer.speakerNotes': string;
  'homeHero.footer.noSpeakerNotes': string;
  'homeHero.footer.availableCount': string;
  'homeHero.footer.noMatches': string;
  'homeHero.moreShortcuts': string;
  'homeHero.railAria': string;
  'homeHero.subTypeAria': string;
  'homeHero.confirmReplaceTitle': string;
  'homeHero.confirmReplaceBody': string;
  'homeHero.confirmReplace': string;
  'homeHero.chip.prototype': string;
  'homeHero.chip.liveArtifact': string;
  'homeHero.chip.deck': string;
  'homeHero.chip.image': string;
  'homeHero.chip.video': string;
  'homeHero.chip.hyperframes': string;
  'homeHero.chip.audio': string;
  'homeHero.chip.createBrandKit': string;
  'homeHero.chip.createPlugin': string;
  'homeHero.chip.figma': string;
  'homeHero.chip.folder': string;
  'homeHero.chip.template': string;
  'homeHero.chip.liveArtifactHint': string;
  'homeHero.chip.hyperframesHint': string;
  'homeHero.chip.createBrandKitHint': string;
  'homeHero.chip.createPluginHint': string;
  'homeHero.chip.figmaHint': string;
  'homeHero.chip.folderHint': string;
  'homeHero.chip.templateHint': string;
  'homeHero.chip.wireframe': string;
  'homeHero.chip.mobile': string;
  'homeHero.chip.document': string;
  'homeHero.chip.prototypeDesc': string;
  'homeHero.chip.wireframeDesc': string;
  'homeHero.chip.mobileDesc': string;
  'homeHero.chip.deckDesc': string;
  'homeHero.chip.documentDesc': string;
  'homeHero.chip.imageDesc': string;
  'homeHero.chip.videoDesc': string;
  'homeHero.chip.audioDesc': string;
  'homeHero.chip.hyperframesDesc': string;
  'homeHero.chip.liveArtifactDesc': string;
  'homeHero.chip.createBrandKitDesc': string;
  'homeHero.chip.prototypeNext': string;
  'homeHero.chip.wireframeNext': string;
  'homeHero.chip.mobileNext': string;
  'homeHero.chip.deckNext': string;
  'homeHero.chip.documentNext': string;
  'homeHero.chip.imageNext': string;
  'homeHero.chip.videoNext': string;
  'homeHero.chip.audioNext': string;
  'homeWorkingDir.trigger': string;
  'homeWorkingDir.pick': string;
  'homeWorkingDir.replace': string;
  'homeWorkingDir.recent': string;
  'homeWorkingDir.recentEmpty': string;
  'homeWorkingDir.clear': string;
  'homeWorkingDir.hint': string;
  'homeWorkingDir.missing': string;
  'homeWorkingDir.applyFailed': string;
  'workingDirPicker.title': string;
  'workingDirPicker.homeTitle': string;
  'workingDirPicker.processing': string;
  'workingDirPicker.select': string;
  'workingDirPicker.clearAria': string;
  'workingDirPicker.replaceFailed': string;
  'workingDirPicker.unavailable': string;
  'workingDirPicker.openUnavailable': string;
  'workingDirPicker.openFailed': string;
  'workingDirPicker.showInFileManager': string;
  'workingDirPicker.replace': string;
  'workingDirPicker.recent': string;
  'workingDirPicker.defaultLabel': string;
  'handoff.toTarget': string;
  'handoff.openInTarget': string;
  'handoff.openAction': string;
  'handoff.menuTitle': string;
  'handoff.action': string;
  'handoff.fallbackTitle': string;
  'handoff.chooseTargetAria': string;
  'handoff.optionsAria': string;
  'handoff.editorSection': string;
  'handoff.cliSection': string;
  'handoff.clickOpen': string;
  'handoff.framework': string;
  'handoff.framework.react': string;
  'handoff.framework.vue': string;
  'handoff.framework.svelte': string;
  'handoff.framework.solid': string;
  'handoff.framework.next': string;
  'handoff.framework.vanilla': string;
  'handoff.frameworkPrompt.react': string;
  'handoff.frameworkPrompt.vue': string;
  'handoff.frameworkPrompt.svelte': string;
  'handoff.frameworkPrompt.solid': string;
  'handoff.frameworkPrompt.next': string;
  'handoff.frameworkPrompt.vanilla': string;
  'handoff.amrWebsite': string;
  'handoff.copyPrompt': string;
  'handoff.copyPromptForTarget': string;
  'handoff.copied': string;
  'handoff.projectPathUnavailable': string;
  'handoff.copyFailed': string;
  'handoff.promptIntro': string;
  'handoff.promptTarget': string;
  'handoff.promptCli': string;
  'handoff.promptStepsLead': string;
  'handoff.promptReadFiles': string;
  'handoff.promptKeepDesign': string;
  'handoff.promptProduceCode': string;
  'handoff.promptVerify': string;
  'handoff.promptCommandHint': string;
  'handoff.promptProject': string;
  'handoff.promptProjectId': string;
  'handoff.notInstalled': string;
  'handoff.notDetectedTitle': string;
  'designSystemPicker.select': string;
  'designSystemPicker.loading': string;
  'designSystemPicker.searchPlaceholder': string;
  'designSystemPicker.searchCompactPlaceholder': string;
  'designSystemPicker.noneTitle': string;
  'designSystemPicker.noneSummary': string;
  'designSystemPicker.empty': string;
  'designSystemPicker.openPreview': string;
  'designSystemPicker.loadingPreview': string;
  'designSystemPicker.noPreview': string;
  'designSystemPicker.previewHint': string;
  'designSystemPicker.fullscreenAria': string;
  'designSystemPicker.closeFullscreen': string;
  'designSystemPicker.closeEsc': string;
  'designSystemPicker.previewFrameTitle': string;
  'designSystemPicker.fullscreenFrameTitle': string;
  'recentProjects.title': string;
  'recentProjects.viewAll': string;
  'recentProjects.empty': string;
  'pluginsHome.title': string;
  'pluginsHome.subtitle': string;
  'pluginsHome.browseRegistry': string;
  'pluginsHome.count': string;
  'pluginsHome.loadingCatalog': string;
  'pluginsHome.emptyCatalog': string;
  'pluginsHome.emptyFiltered': string;
  'pluginsHome.clearFilters': string;
  'pluginsHome.modeAria': string;
  'pluginsHome.featured': string;
  'pluginsHome.totalInCatalog': string;
  'pluginsHome.categoryFilterAria': string;
  'pluginsHome.subcategoryFilterAria': string;
  'pluginsHome.allCategory': string;
  'pluginsHome.searchPlaceholder': string;
  'pluginsHome.searchAria': string;
  'pluginsHome.clearSearch': string;
  'pluginsHome.facet.import': string;
  'pluginsHome.facet.create': string;
  'pluginsHome.facet.export': string;
  'pluginsHome.facet.share': string;
  'pluginsHome.facet.deploy': string;
  'pluginsHome.facet.refine': string;
  'pluginsHome.facet.extend': string;
  'pluginsHome.facet.figma': string;
  'pluginsHome.facet.github': string;
  'pluginsHome.facet.codeFolder': string;
  'pluginsHome.facet.url': string;
  'pluginsHome.facet.screenshot': string;
  'pluginsHome.facet.pdf': string;
  'pluginsHome.facet.pptx': string;
  'pluginsHome.facet.framer': string;
  'pluginsHome.facet.webflow': string;
  'pluginsHome.facet.slides': string;
  'pluginsHome.facet.publicLink': string;
  'pluginsHome.facet.githubPr': string;
  'pluginsHome.facet.githubGist': string;
  'pluginsHome.subfacet.business-dashboards': string;
  'pluginsHome.subfacet.app-prototypes': string;
  'pluginsHome.subfacet.landing-marketing': string;
  'pluginsHome.subfacet.developer-tools': string;
  'pluginsHome.subfacet.docs-reports': string;
  'pluginsHome.subfacet.brand-design': string;
  'pluginsHome.subfacet.pitch-business': string;
  'pluginsHome.subfacet.course-training': string;
  'pluginsHome.subfacet.reports-briefings': string;
  'pluginsHome.subfacet.product-sales': string;
  'pluginsHome.subfacet.engineering-talks': string;
  'pluginsHome.subfacet.creative-decks': string;
  'pluginsHome.subfacet.ui-product-mockups': string;
  'pluginsHome.subfacet.brand-visuals': string;
  'pluginsHome.subfacet.storyboards-motion-refs': string;
  'pluginsHome.subfacet.social-content': string;
  'pluginsHome.subfacet.avatar-portrait': string;
  'pluginsHome.subfacet.illustration-style': string;
  'pluginsHome.subfacet.motion-effects': string;
  'pluginsHome.subfacet.social-short-form': string;
  'pluginsHome.subfacet.marketing-product': string;
  'pluginsHome.subfacet.data-explainers': string;
  'pluginsHome.subfacet.cinematic-story': string;
  'pluginsView.lede': string;
  'pluginsView.importPlugin': string;
  'pluginsView.agentContext': string;
  'pluginsView.summaryAria': string;
  'pluginsView.areasAria': string;
  'pluginsView.loading': string;
  'pluginsView.tab.installed': string;
  'pluginsView.tab.available': string;
  'pluginsView.tab.sources': string;
  'pluginsView.tab.team': string;
  'pluginsView.tabHint.installed': string;
  'pluginsView.tabHint.available': string;
  'pluginsView.tabHint.sources': string;
  'pluginsView.tabHint.team': string;
  'pluginsView.installedTitle': string;
  'pluginsView.installedSubtitle': string;
  'pluginsView.installedEmpty': string;
  'pluginsView.availableTitle': string;
  'pluginsView.availableSubtitle': string;
  'pluginsView.availableFiltersAria': string;
  'pluginsView.searchAvailableAria': string;
  'pluginsView.searchAvailablePlaceholder': string;
  'pluginsView.clearAvailableSearch': string;
  'pluginsView.source': string;
  'pluginsView.availableEmptyInstalled': string;
  'pluginsView.availableEmptyFiltered': string;
  'pluginsView.availableEmptyNoSources': string;
  'pluginsView.installing': string;
  'pluginsView.install': string;
  'pluginsView.sourcesTitle': string;
  'pluginsView.sourcesSubtitle': string;
  'pluginsView.sourceUrl': string;
  'pluginsView.defaultTrust': string;
  'pluginsView.trust.restricted': string;
  'pluginsView.trust.trusted': string;
  'pluginsView.trust.official': string;
  'pluginsView.adding': string;
  'pluginsView.addSource': string;
  'pluginsView.sourcesEmpty': string;
  'pluginsView.pluginsCount': string;
  'pluginsView.catalogVersion': string;
  'pluginsView.trustFor': string;
  'pluginsView.refreshing': string;
  'pluginsView.removing': string;
  'pluginsView.teamTitle': string;
  'pluginsView.teamBody': string;
  'pluginCard.details': string;
  'pluginCard.use': string;
  'pluginCard.useWithQuery': string;
  'pluginCard.applying': string;
  'pluginCard.duplicate': string;
  'pluginCard.duplicating': string;
  'pluginCard.duplicateFailed': string;
  'pluginCard.saved': string;
  'pluginCard.publish': string;
  'pluginCard.contribute': string;
  'pluginCard.starting': string;
  'pluginCard.detailsAria': string;
  'pluginCard.chooseUseAria': string;
  'pluginCard.useOptionsAria': string;
  'pluginCard.duplicateAria': string;
  'pluginCard.saveAria': string;
  'pluginCard.savedAria': string;
  'pluginCard.shareAria': string;
  'pluginCard.publishAria': string;
  'pluginCard.publishTitle': string;
  'pluginCard.contributeAria': string;
  'pluginCard.contributeTitle': string;
  'skillPluginCandidate.createForMe': string;
  'skillPluginCandidate.contributeToMain': string;
  'skillPluginCandidate.repoDescription': string;
  'integrations.kicker': string;
  'integrations.lede': string;
  'integrations.agentReady': string;
  'integrations.areasAria': string;
  'integrations.tabLabel.mcp': string;
  'integrations.tabLabel.skills': string;
  'integrations.tabHint.mcp': string;
  'integrations.tabHint.connectors': string;
  'integrations.tabHint.useEverywhere': string;
  'integrations.skillsTitle': string;
  'integrations.skillsBody': string;
  'mcpClient.title': string;
  'mcpClient.subtitle': string;
  'mcpClient.addServer': string;
  'mcpClient.emptyTitle': string;
  'mcpClient.emptyBody': string;
  'mcpClient.saveChanges': string;
  'mcpClient.storedAt': string;
  'mcpClient.daemonError': string;
  'mcpClient.saveFailed': string;
  'useEverywhere.modalAria': string;
  'useEverywhere.modalTitle': string;
  'useEverywhere.modalSubtitle': string;
  'useEverywhere.closeAria': string;
  'useEverywhere.closeTitle': string;
  'useEverywhere.tabsAria': string;
  'useEverywhere.footStrong': string;
  'useEverywhere.footBody': string;
  'useEverywhere.configureMcp': string;
  'useEverywhere.copyGuide': string;
  'useEverywhere.copy': string;
  'useEverywhere.copied': string;
  'useEverywhere.copyFailed': string;
  'useEverywhere.copySnippetAria': string;
  'useEverywhere.section.overview.tab': string;
  'useEverywhere.section.overview.heading': string;
  'useEverywhere.section.overview.intro': string;
  'useEverywhere.section.overview.bullet1': string;
  'useEverywhere.section.overview.bullet2': string;
  'useEverywhere.section.overview.bullet3': string;
  'useEverywhere.section.overview.bullet4': string;
  'useEverywhere.section.overview.bullet5': string;
  'useEverywhere.section.overview.snippet1': string;
  'useEverywhere.section.overview.snippet2': string;
  'useEverywhere.section.overview.snippet3': string;
  'useEverywhere.section.overview.footer': string;
  'useEverywhere.section.cli.tab': string;
  'useEverywhere.section.cli.heading': string;
  'useEverywhere.section.cli.intro': string;
  'useEverywhere.section.cli.bullet1': string;
  'useEverywhere.section.cli.bullet2': string;
  'useEverywhere.section.cli.bullet3': string;
  'useEverywhere.section.cli.bullet4': string;
  'useEverywhere.section.cli.bullet5': string;
  'useEverywhere.section.cli.bullet6': string;
  'useEverywhere.section.cli.snippet1': string;
  'useEverywhere.section.cli.snippet2': string;
  'useEverywhere.section.cli.snippet3': string;
  'useEverywhere.section.cli.snippet4': string;
  'useEverywhere.section.cli.snippet5': string;
  'useEverywhere.section.cli.footer': string;
  'useEverywhere.section.mcp.tab': string;
  'useEverywhere.section.mcp.heading': string;
  'useEverywhere.section.mcp.intro': string;
  'useEverywhere.section.mcp.bullet1': string;
  'useEverywhere.section.mcp.bullet2': string;
  'useEverywhere.section.mcp.bullet3': string;
  'useEverywhere.section.mcp.bullet4': string;
  'useEverywhere.section.mcp.snippet1': string;
  'useEverywhere.section.mcp.snippet2': string;
  'useEverywhere.section.mcp.snippet3': string;
  'useEverywhere.section.mcp.footer': string;
  'useEverywhere.section.http.tab': string;
  'useEverywhere.section.http.heading': string;
  'useEverywhere.section.http.intro': string;
  'useEverywhere.section.http.bullet1': string;
  'useEverywhere.section.http.bullet2': string;
  'useEverywhere.section.http.bullet3': string;
  'useEverywhere.section.http.bullet4': string;
  'useEverywhere.section.http.bullet5': string;
  'useEverywhere.section.http.bullet6': string;
  'useEverywhere.section.http.snippet1': string;
  'useEverywhere.section.http.snippet2': string;
  'useEverywhere.section.http.snippet3': string;
  'useEverywhere.section.http.footer': string;
  'useEverywhere.section.skills.tab': string;
  'useEverywhere.section.skills.heading': string;
  'useEverywhere.section.skills.intro': string;
  'useEverywhere.section.skills.bullet1': string;
  'useEverywhere.section.skills.bullet2': string;
  'useEverywhere.section.skills.bullet3': string;
  'useEverywhere.section.skills.bullet4': string;
  'useEverywhere.section.skills.bullet5': string;
  'useEverywhere.section.skills.snippet1': string;
  'useEverywhere.section.skills.snippet2': string;
  'useEverywhere.section.skills.snippet3': string;
  'useEverywhere.section.skills.snippet4': string;
  'useEverywhere.section.skills.footer': string;

  // Automations entry view
  'tasks.kicker': string;
  'tasks.comingSoon': string;
  'tasks.lede': string;
  'tasks.newAutomation': string;
  'tasks.previewNote': string;
  'tasks.primitivesAria': string;
  'tasks.primitive.orbit.title': string;
  'tasks.primitive.orbit.body': string;
  'tasks.primitive.orbit.enabled': string;
  'tasks.primitive.orbit.manualOnly': string;
  'tasks.primitive.routines.title': string;
  'tasks.primitive.routines.body': string;
  'tasks.primitive.routines.meta': string;
  'tasks.primitive.schedules.title': string;
  'tasks.primitive.schedules.body': string;
  'tasks.primitive.schedules.meta': string;
  'tasks.primitive.liveArtifacts.title': string;
  'tasks.primitive.liveArtifacts.body': string;
  'tasks.primitive.liveArtifacts.meta': string;
  'tasks.listAria': string;
  'tasks.routinesAndRuns': string;
  'tasks.filtersAria': string;
  'tasks.filter.scheduled': string;
  'tasks.filter.running': string;
  'tasks.filter.done': string;
  'tasks.configurationAria': string;
  'tasks.slot.trigger': string;
  'tasks.slot.pattern': string;
  'tasks.slot.runtime': string;
  'tasks.slot.output': string;
  'tasks.liveArtifact': string;
  'tasks.viewProgress': string;
  'tasks.pause': string;
  'tasks.runNow': string;
  'tasks.openArtifact': string;
  'tasks.status.dailyAt': string;
  'tasks.status.pausedManual': string;
  'tasks.sample.orbit.title': string;
  'tasks.sample.orbit.metaEnabled': string;
  'tasks.sample.orbit.metaDisabled': string;
  'tasks.sample.orbit.previewEnabled': string;
  'tasks.sample.orbit.previewDisabled': string;
  'tasks.sample.orbit.triggerEnabled': string;
  'tasks.sample.orbit.triggerDisabled': string;
  'tasks.sample.orbit.pattern': string;
  'tasks.sample.orbit.runtime': string;
  'tasks.sample.orbit.output': string;
  'tasks.sample.orbit.artifactMetaEnabled': string;
  'tasks.sample.orbit.artifactMetaDisabled': string;
  'tasks.sample.orbit.body1': string;
  'tasks.sample.orbit.body2': string;
  'tasks.sample.orbit.body3': string;
  'tasks.sample.mcp.title': string;
  'tasks.sample.mcp.status': string;
  'tasks.sample.mcp.meta': string;
  'tasks.sample.mcp.preview': string;
  'tasks.sample.mcp.trigger': string;
  'tasks.sample.mcp.pattern': string;
  'tasks.sample.mcp.runtime': string;
  'tasks.sample.mcp.output': string;
  'tasks.sample.mcp.artifactMeta': string;
  'tasks.sample.mcp.body1': string;
  'tasks.sample.mcp.body2': string;
  'tasks.sample.mcp.body3': string;
  'tasks.sample.mcp.body4': string;
  'tasks.sample.mcp.body5': string;
  'tasks.sample.weekly.title': string;
  'tasks.sample.weekly.status': string;
  'tasks.sample.weekly.meta': string;
  'tasks.sample.weekly.preview': string;
  'tasks.sample.weekly.trigger': string;
  'tasks.sample.weekly.pattern': string;
  'tasks.sample.weekly.runtime': string;
  'tasks.sample.weekly.output': string;
  'tasks.sample.weekly.artifactMeta': string;
  'tasks.sample.weekly.body1': string;
  'tasks.sample.weekly.body2': string;
  'tasks.sample.weekly.body3': string;
  'tasks.sample.weekly.body4': string;
  'tasks.sample.weekly.body5': string;
  'tasks.sample.pr.title': string;
  'tasks.sample.pr.status': string;
  'tasks.sample.pr.meta': string;
  'tasks.sample.pr.preview': string;
  'tasks.sample.pr.trigger': string;
  'tasks.sample.pr.pattern': string;
  'tasks.sample.pr.runtime': string;
  'tasks.sample.pr.output': string;
  'tasks.sample.pr.artifactMeta': string;
  'tasks.sample.pr.body1': string;
  'tasks.sample.pr.body2': string;
  'tasks.sample.pr.body3': string;
  'tasks.sample.meeting.title': string;
  'tasks.sample.meeting.status': string;
  'tasks.sample.meeting.meta': string;
  'tasks.sample.meeting.preview': string;
  'tasks.sample.meeting.trigger': string;
  'tasks.sample.meeting.pattern': string;
  'tasks.sample.meeting.runtime': string;
  'tasks.sample.meeting.output': string;
  'tasks.sample.meeting.artifactMeta': string;
  'tasks.sample.meeting.body1': string;
  'tasks.sample.meeting.body2': string;
  'tasks.sample.meeting.body3': string;
  'tasks.sample.candidate.title': string;
  'tasks.sample.candidate.status': string;
  'tasks.sample.candidate.meta': string;
  'tasks.sample.candidate.preview': string;
  'tasks.sample.candidate.trigger': string;
  'tasks.sample.candidate.pattern': string;
  'tasks.sample.candidate.runtime': string;
  'tasks.sample.candidate.output': string;
  'tasks.sample.candidate.artifactMeta': string;
  'tasks.sample.candidate.body1': string;
  'tasks.sample.candidate.body2': string;
  'tasks.sample.candidate.body3': string;
  // Routines / Automations settings page (issue #1372)
  'routines.title': string;
  'routines.subtitle': string;
  'routines.newAutomation': string;
  'routines.loading': string;
  'routines.empty': string;
  'routines.emptyHint': string;
  'routines.runHistoryLoading': string;
  'routines.runHistoryEmpty': string;
  'routines.fieldName': string;
  'routines.fieldNamePlaceholder': string;
  'routines.fieldPrompt': string;
  'routines.fieldPromptPlaceholder': string;
  'routines.fieldSchedule': string;
  'routines.fieldMinute': string;
  'routines.fieldTime': string;
  'routines.fieldTimezone': string;
  'routines.fieldsetProject': string;
  'routines.kind.hourly': string;
  'routines.kind.daily': string;
  'routines.kind.weekdays': string;
  'routines.kind.weekly': string;
  'routines.weekday.short.0': string;
  'routines.weekday.short.1': string;
  'routines.weekday.short.2': string;
  'routines.weekday.short.3': string;
  'routines.weekday.short.4': string;
  'routines.weekday.short.5': string;
  'routines.weekday.short.6': string;
  'routines.weekday.long.0': string;
  'routines.weekday.long.1': string;
  'routines.weekday.long.2': string;
  'routines.weekday.long.3': string;
  'routines.weekday.long.4': string;
  'routines.weekday.long.5': string;
  'routines.weekday.long.6': string;
  'routines.timeAm': string;
  'routines.timePm': string;
  'routines.describe.hourly': string;
  'routines.describe.daily': string;
  'routines.describe.weekdays': string;
  'routines.describe.weekly': string;
  'routines.modeCreate': string;
  'routines.modeCreateHint': string;
  'routines.modeReuse': string;
  'routines.modeReuseHint': string;
  'routines.pickProject': string;
  'routines.targetReuse': string;
  'routines.targetCreate': string;
  'routines.tagPaused': string;
  'routines.metaNext': string;
  'routines.metaLast': string;
  'routines.triggerManual': string;
  'routines.triggerScheduled': string;
  'routines.openProject': string;
  'routines.openProjectTitle': string;
  'routines.runNow': string;
  'routines.edit': string;
  'routines.pause': string;
  'routines.resume': string;
  'routines.history': string;
  'routines.hideHistory': string;
  'routines.delete': string;
  'routines.deleteTitle': string;
  'routines.cancel': string;
  'routines.save': string;
  'routines.saving': string;
  'routines.create': string;
  'routines.creating': string;
  'routines.status.queued': string;
  'routines.status.running': string;
  'routines.status.succeeded': string;
  'routines.status.failed': string;
  'routines.status.canceled': string;
  'routines.confirmDelete': string;
  'routines.errorPickProject': string;
  'routines.errorAgentEmptyOutput': string;
  // Automations tab (TasksView)
  'automations.eyebrow': string;
  'automations.title': string;
  'automations.lede': string;
  'automations.summaryAria': string;
  'automations.metricActive': string;
  'automations.metricPaused': string;
  'automations.metricTemplates': string;
  'automations.newAutomation': string;
  'automations.yourAutomations': string;
  'automations.loading': string;
  'automations.emptyTitle': string;
  'automations.emptyBody': string;
  'automations.targetNewEachRun': string;
  'automations.lastRun': string;
  'automations.openResult': string;
  'automations.run': string;
  'automations.runNowTitle': string;
  'automations.history': string;
  'automations.hideHistory': string;
  'automations.edit': string;
  'automations.pause': string;
  'automations.resume': string;
  'automations.deleteAria': string;
  'automations.deleteTitle': string;
  'automations.deleteConfirm': string;
  'automations.proposalsTitle': string;
  'automations.proposalsSub': string;
  'automations.proposalsPending': string;
  'automations.apply': string;
  'automations.reject': string;
  'automations.proposalsDismissReason': string;
  'automations.templatesTitle': string;
  'automations.templatesSub': string;
  'automations.templatesCount': string;
  'automations.templateFiltersAria': string;
  'automations.templatesEmptyTitle': string;
  'automations.templatesEmptyBody': string;
  'automations.useTemplate': string;
  'automations.runHistoryLoading': string;
  'automations.runHistoryEmpty': string;
  'automations.runHistoryTitle': string;
  'automations.runHistoryLatest': string;
  'automations.crystallize': string;
  'automations.crystallizing': string;
  'automations.crystallizeTitle': string;
  'automations.crystallizeFailed': string;
  'automations.crystallizePartialSuccess': string;
  'automations.crystallizeRefreshFailed': string;
  'automations.crystallizeNoProposals': string;
  'automations.openConversation': string;
  'automations.runHistoryAria': string;
  'automations.proposalsAria': string;
  'automations.templatesAria': string;
  'automations.runInProgress': string;
  'automations.scheduleStatusPaused': string;
  'automations.nextRunManualOnly': string;
  'automations.nextRunScheduled': string;
  'automations.nextRunAt': string;
  'automations.statusSucceeded': string;
  'automations.statusFailed': string;
  'automations.statusRunning': string;
  'automations.statusQueued': string;
  'automations.statusCanceled': string;
  'automations.kindOrbit': string;
  'automations.kindLiveArtifact': string;
  'automations.kindAutomation': string;
  'automations.proposalTargetMemory': string;
  'automations.proposalTargetDesignSystem': string;
  'automations.proposalTargetSkill': string;
  'automations.proposalTargetTemplate': string;
  'automations.proposalActionCreate': string;
  'automations.proposalActionUpdate': string;
  'automations.proposalActionMerge': string;
  'automations.proposalActionMove': string;
  'automations.proposalActionDelete': string;
  'automations.proposalActionPromote': string;
  'automations.filterAll': string;
  'automations.filterOrbit': string;
  'automations.filterLiveArtifacts': string;
  'automations.filterMemory': string;
  'automations.filterDesignSystems': string;
  'automations.filterSkills': string;
  'automations.filterConnectors': string;
  'automations.filterCompression': string;
  'automations.filterRelease': string;
  'automations.filterQuality': string;
  'automations.tpl.memoryRefresh.title': string;
  'automations.tpl.memoryRefresh.desc': string;
  'automations.tpl.designSystemRefresh.title': string;
  'automations.tpl.designSystemRefresh.desc': string;
  'automations.tpl.liveArtifactRegistry.title': string;
  'automations.tpl.liveArtifactRegistry.desc': string;
  'automations.tpl.orbitDashboard.title': string;
  'automations.tpl.orbitDashboard.desc': string;
  'automations.tpl.releaseNotes.title': string;
  'automations.tpl.releaseNotes.desc': string;
  'automations.tpl.qualityRegressionWatch.title': string;
  'automations.tpl.qualityRegressionWatch.desc': string;
  'automations.tpl.orbitDaily.title': string;
  'automations.tpl.orbitDaily.desc': string;
  'automations.tpl.liveStatusBoard.title': string;
  'automations.tpl.liveStatusBoard.desc': string;
  // Design systems manager tab (DesignSystemsTab)
  'dsManager.areaAria': string;
  'dsManager.tabDesignSystem': string;
  'dsManager.tabTemplate': string;
  'dsManager.sourceAria': string;
  'dsManager.templateSourceAria': string;
  'dsManager.yourSystems': string;
  'dsManager.officialPresets': string;
  'dsManager.enterprise': string;
  'dsManager.yourTemplates': string;
  'dsManager.yourSystemsAria': string;
  'dsManager.eyebrowDesignSystems': string;
  'dsManager.yourSystemsHeading': string;
  'dsManager.filterAria': string;
  'dsManager.filterAll': string;
  'dsManager.filterPublished': string;
  'dsManager.filterDraft': string;
  'dsManager.createTitle': string;
  'dsManager.createBody': string;
  'dsManager.createAction': string;
  'dsManager.emptyMine': string;
  'dsManager.badgeDefault': string;
  'dsManager.rowMetaUpdated': string;
  'dsManager.edit': string;
  'dsManager.openSystem': string;
  'dsManager.editWithAgent': string;
  'dsManager.downloadTitle': string;
  'dsManager.downloadAria': string;
  'dsManager.downloadFailed': string;
  'dsManager.deleteConfirm': string;
  'dsManager.makeDefault': string;
  'dsManager.statusPublished': string;
  'dsManager.statusDraft': string;
  'dsManager.openSystemAria': string;
  'dsManager.deleteSystemAria': string;
  'dsManager.presetsAria': string;
  'dsManager.eyebrowLibrary': string;
  'dsManager.eyebrowTemplates': string;
  'dsManager.yourTemplatesAria': string;
  'dsManager.emptyTemplates': string;
  'dsManager.templateDescFallback': string;
  'dsManager.comingSoonBadge': string;
  'dsManager.enterpriseDsTitle': string;
  'dsManager.enterpriseDsBody': string;
  'dsManager.enterpriseTplTitle': string;
  'dsManager.enterpriseTplBody': string;
  // Settings instructions + MCP forwarding banner
  'settings.instructionsTitle': string;
  'settings.instructionsSubtitle': string;
  'settings.instructionsNavSub': string;
  'settings.customInstructionsDesc': string;
  'mcpClient.forwardedToLabel': string;
  'mcpClient.notForwardedToLabel': string;
  'mcpClient.forwardedAcpNote': string;
  'mcpClient.notForwardedNote': string;
  // Bottom-of-rail help menu
  'entry.helpAria': string;
  'entry.helpMenuAria': string;
  'entry.helpGetHelp': string;
  'entry.helpSubmitFeature': string;
  'entry.helpWhatsNew': string;
  'entry.helpDownloadDesktop': string;
  // GitHub star pill in the top bar
  'entry.githubStarLabel': string;
  'entry.githubStarTitle': string;
  'entry.githubStarAria': string;

  // Connectors tab
  'connectors.title': string;
  'connectors.subtitle': string;
  'connectors.account': string;
  'connectors.noAccount': string;
  'connectors.tools': string;
  'connectors.connect': string;
  'connectors.disconnect': string;
  'connectors.authorizationPending': string;
  'connectors.authorizationPendingHint': string;
  'connectors.cancelAuthorization': string;
  'connectors.configure': string;
  'connectors.unavailable': string;
  'connectors.phaseStubTitle': string;
  'connectors.statusAvailable': string;
  'connectors.statusConnected': string;
  'connectors.statusError': string;
  'connectors.statusDisabled': string;
  'connectors.gateTitle': string;
  'connectors.gateBody': string;
  'connectors.aboutLabel': string;
  'connectors.detailsLabel': string;
  'connectors.statusLabel': string;
  'connectors.categoryLabel': string;
  'connectors.category.aiAgents': string;
  'connectors.category.aiInfrastructure': string;
  'connectors.category.accounting': string;
  'connectors.category.admin': string;
  'connectors.category.advertising': string;
  'connectors.category.analytics': string;
  'connectors.category.automation': string;
  'connectors.category.cms': string;
  'connectors.category.crm': string;
  'connectors.category.calendar': string;
  'connectors.category.commerce': string;
  'connectors.category.communication': string;
  'connectors.category.contacts': string;
  'connectors.category.dataPlatform': string;
  'connectors.category.database': string;
  'connectors.category.design': string;
  'connectors.category.developer': string;
  'connectors.category.documentation': string;
  'connectors.category.erp': string;
  'connectors.category.education': string;
  'connectors.category.email': string;
  'connectors.category.events': string;
  'connectors.category.fieldService': string;
  'connectors.category.finance': string;
  'connectors.category.fitness': string;
  'connectors.category.forms': string;
  'connectors.category.gaming': string;
  'connectors.category.hr': string;
  'connectors.category.hospitality': string;
  'connectors.category.itsm': string;
  'connectors.category.integration': string;
  'connectors.category.localization': string;
  'connectors.category.logistics': string;
  'connectors.category.maps': string;
  'connectors.category.marketing': string;
  'connectors.category.media': string;
  'connectors.category.meetings': string;
  'connectors.category.nonprofit': string;
  'connectors.category.observability': string;
  'connectors.category.payments': string;
  'connectors.category.personal': string;
  'connectors.category.presentations': string;
  'connectors.category.procurement': string;
  'connectors.category.product': string;
  'connectors.category.productivity': string;
  'connectors.category.projectManagement': string;
  'connectors.category.recruiting': string;
  'connectors.category.research': string;
  'connectors.category.salesIntelligence': string;
  'connectors.category.scheduling': string;
  'connectors.category.search': string;
  'connectors.category.security': string;
  'connectors.category.signing': string;
  'connectors.category.social': string;
  'connectors.category.spreadsheets': string;
  'connectors.category.storage': string;
  'connectors.category.support': string;
  'connectors.category.surveys': string;
  'connectors.category.tasks': string;
  'connectors.category.timeTracking': string;
  'connectors.category.video': string;
  'connectors.category.whiteboard': string;
  'connectors.providerLabel': string;
  'connectors.toolsSection': string;
  'connectors.toolsLoading': string;
  'connectors.noToolsAvailable': string;
  'connectors.toolDetailsUnavailable': string;
  'connectors.loadMoreTools': string;
  'connectors.openDetailsAria': string;
  'connectors.toolsBadgeNone': string;
  'connectors.toolsBadgeOne': string;
  'connectors.toolsBadgeMany': string;
  'connectors.searchPlaceholder': string;
  'connectors.searchAriaLabel': string;
  'connectors.searchClear': string;
  'connectors.emptyNoMatchTitle': string;
  'connectors.emptyNoMatchBody': string;
  'connectors.emptyNoMatchAction': string;

  // New project panel
  'newproj.tabPrototype': string;
  'newproj.tabLiveArtifact': string;
  'newproj.tabDeck': string;
  'newproj.tabTemplate': string;
  'newproj.tabMedia': string;
  'newproj.tabOther': string;
  'newproj.titlePrototype': string;
  'newproj.titleLiveArtifact': string;
  'newproj.titleDeck': string;
  'newproj.titleTemplate': string;
  'newproj.titleImage': string;
  'newproj.titleVideo': string;
  'newproj.titleAudio': string;
  'newproj.titleMedia': string;
  'newproj.titleOther': string;
  'newproj.namePlaceholder': string;
  'newproj.fidelityLabel': string;
  'newproj.fidelityWireframe': string;
  'newproj.fidelityHigh': string;
  'newproj.toggleSpeakerNotes': string;
  'newproj.toggleSpeakerNotesHint': string;
  'newproj.toggleAnimations': string;
  'newproj.toggleAnimationsHint': string;
  'newproj.targetPlatformsLabel': string;
  'newproj.targetPlatformsHint': string;
  'newproj.platform.responsive.label': string;
  'newproj.platform.responsive.hint': string;
  'newproj.platform.webDesktop.label': string;
  'newproj.platform.webDesktop.hint': string;
  'newproj.platform.mobileIos.label': string;
  'newproj.platform.mobileIos.hint': string;
  'newproj.platform.mobileAndroid.label': string;
  'newproj.platform.mobileAndroid.hint': string;
  'newproj.platform.tablet.label': string;
  'newproj.platform.tablet.hint': string;
  'newproj.platform.desktopApp.label': string;
  'newproj.platform.desktopApp.hint': string;
  'newproj.surfaceOptionsLabel': string;
  'newproj.includeLandingPage': string;
  'newproj.includeLandingPageHint': string;
  'newproj.includeOsWidgets': string;
  'newproj.includeOsWidgetsHint': string;
  'newproj.includeOsWidgetsDisabledHint': string;
  'newproj.templateLabel': string;
  'newproj.noTemplatesTitle': string;
  'newproj.noTemplatesBody': string;
  'newproj.savedTemplate': string;
  'newproj.fileSingular': string;
  'newproj.filePlural': string;
  'newproj.create': string;
  'newproj.locationLabel': string;
  'newproj.locationDefault': string;
  'newproj.locationExternalBase': string;
  'newproj.createLiveArtifact': string;
  'newproj.createFromTemplate': string;
  'newproj.createDisabledTitle': string;
  'newproj.importClaudeZip': string;
  'newproj.importClaudeZipTitle': string;
  'newproj.importingClaudeZip': string;
  'newproj.privacyFooter': string;
  'newproj.designSystem': string;
  'newproj.dsNoneFreeform': string;
  'newproj.dsNoneSubtitleEmpty': string;
  'newproj.dsNoneSubtitleSelected': string;
  'newproj.dsCategoryFallback': string;
  'newproj.dsSearch': string;
  'newproj.dsModeAria': string;
  'newproj.dsModeSingle': string;
  'newproj.dsModeMulti': string;
  'newproj.dsNoneTitle': string;
  'newproj.dsNoneSub': string;
  'newproj.dsEmpty': string;
  'newproj.dsFootSingular': string;
  'newproj.dsFootPlural': string;
  'newproj.dsFootClear': string;
  'newproj.dsBadgeDefault': string;
  'newproj.dsPrimaryFallback': string;
  'newproj.surfaceImage': string;
  'newproj.surfaceVideo': string;
  'newproj.surfaceAudio': string;
  'newproj.modelLabel': string;
  'newproj.modelSearch': string;
  'newproj.modelEmpty': string;
  'newproj.modelRecommended': string;
  'newproj.modelMissingTitle': string;
  'newproj.modelMissingSub': string;
  'newproj.aspectLabel': string;
  'newproj.videoLengthLabel': string;
  'newproj.videoLengthSeconds': string;
  'newproj.audioKindLabel': string;
  'newproj.audioKindMusic': string;
  'newproj.audioKindSpeech': string;
  'newproj.audioKindSfx': string;
  'newproj.audioDurationLabel': string;
  'newproj.audioDurationSeconds': string;
  'newproj.voiceLabel': string;
  'newproj.voicePlaceholder': string;
  'newproj.connectorsLabel': string;
  'newproj.connectorsHint': string;
  'newproj.connectorsEmptyTitle': string;
  'newproj.connectorsEmptyBody': string;
  'newproj.connectorsEmptyCta': string;
  'newproj.connectorsLoading': string;
  'newproj.connectorsCountOne': string;
  'newproj.connectorsCountMany': string;
  'newproj.connectorsManage': string;
  'newproj.promptTemplateLabel': string;
  'newproj.promptTemplateNoneTitle': string;
  'newproj.promptTemplateNoneSub': string;
  'newproj.promptTemplateRefSub': string;
  'newproj.promptTemplateSearch': string;
  'newproj.promptTemplateEmpty': string;
  'newproj.promptTemplateBodyLabel': string;
  'newproj.promptTemplateOptimizeHint': string;
  'newproj.promptTemplateBodyEmpty': string;
  'newproj.deleteTemplateTitle': string;
  'newproj.deleteTemplateConfirm': string;
  'newproj.deleteTemplateConfirmCta': string;
  'newproj.deleteTemplateError': string;

  // Prompt templates
  'promptTemplates.searchPlaceholder': string;
  'promptTemplates.countLabel': string;
  'promptTemplates.emptyImage': string;
  'promptTemplates.emptyVideo': string;
  'promptTemplates.emptyNoMatch': string;
  'promptTemplates.attributionFooter': string;
  'promptTemplates.openPreviewTitle': string;
  'promptTemplates.sourcePrefix': string;
  'promptTemplates.fetchError': string;
  'promptTemplates.promptLabel': string;
  'promptTemplates.copyPrompt': string;
  'promptTemplates.copyDone': string;
  'promptTemplates.modelHint': string;
  'promptTemplates.openSource': string;
  'promptTemplates.openFullscreen': string;
  'promptTemplates.closeFullscreen': string;
  'promptTemplates.allSources': string;
  'promptTemplates.sourceFilterAria': string;
  'promptTemplates.retry': string;

  // Designs tab
  'designs.subRecent': string;
  'designs.subYours': string;
  'designs.filterAria': string;
  'designs.searchPlaceholder': string;
  'designs.emptyNoProjects': string;
  'designs.emptyNoMatch': string;
  'designs.deleteTitle': string;
  'designs.deleteConfirm': string;
  'designs.cardFreeform': string;
  'designs.badgeLive': string;
  'designs.liveArtifactBadgesAria': string;
  'designs.liveCount': string;
  'designs.statusLive': string;
  'designs.statusArchived': string;
  'designs.statusError': string;
  'designs.statusRefreshing': string;
  'designs.statusRefreshFailed': string;
  'designs.statusRefreshed': string;
  'designs.status.notStarted': string;
  'designs.status.queued': string;
  'designs.status.running': string;
  'designs.status.awaitingInput': string;
  'designs.status.succeeded': string;
  'designs.status.failed': string;
  'designs.status.canceled': string;
  'designs.status.published': string;
  'designs.viewToggleAria': string;
  'designs.viewGrid': string;
  'designs.viewKanban': string;
  'designs.kanbanEmptyColumn': string;
  'designs.deleteAria': string;
  'designs.menuMore': string;
  'designs.menuRename': string;
  'designs.menuDelete': string;
  'designs.renamePrompt': string;
  'designs.selectMode': string;
  'designs.cancelSelect': string;
  'designs.deleteSelected': string;
  'designs.selectedCount': string;
  'designs.deleteSelectedConfirm': string;
  'designs.deleteSelectedSuccess': string;
  'designs.deleteSelectedPartial': string;
  'designs.tagPrototype': string;
  'designs.tagLiveArtifact': string;
  'designs.tagSlide': string;
  'designs.tagMedia': string;
  'designs.renameTitle': string;
  'designs.renameSave': string;
  'designs.renameCancel': string;

  // Examples tab
  'examples.typeLabel': string;
  'examples.surfaceLabel': string;
  'examples.surfaceWeb': string;
  'examples.surfaceImage': string;
  'examples.surfaceVideo': string;
  'examples.surfaceAudio': string;
  'examples.scenarioLabel': string;
  'examples.modeAll': string;
  'examples.modePrototypeDesktop': string;
  'examples.modePrototypeMobile': string;
  'examples.modeDeck': string;
  'examples.modeDocument': string;
  'examples.modeOrbit': string;
  'examples.modeLive': string;
  'examples.scenarioGeneral': string;
  'examples.scenarioEngineering': string;
  'examples.scenarioProduct': string;
  'examples.scenarioDesign': string;
  'examples.scenarioMarketing': string;
  'examples.scenarioSales': string;
  'examples.scenarioFinance': string;
  'examples.scenarioHr': string;
  'examples.scenarioOperations': string;
  'examples.scenarioSupport': string;
  'examples.scenarioLegal': string;
  'examples.scenarioEducation': string;
  'examples.scenarioPersonal': string;
  'examples.searchPlaceholder': string;
  'examples.searchAria': string;
  'examples.emptyNoSkills': string;
  'examples.emptyNoMatch': string;
  'examples.openPreview': string;
  'examples.loadingPreview': string;
  'examples.hoverPreview': string;
  'examples.usePrompt': string;
  'examples.previewModalTitle': string;
  'examples.shareTitle': string;
  'examples.shareLoadFirst': string;
  // Card placeholder + share-button hint for skills whose
  // `od.preview.type` is not `html` (image / markdown / …) so the
  // gallery doesn't sit on a forever "Loading preview…" shimmer for
  // skills that ship no fetchable artifact. Issue #897.
  'examples.unavailablePlaceholder': string;
  'examples.shareUnavailable': string;
  'examples.shareMenu': string;
  'examples.exportPdfAllSlides': string;
  'examples.exportPptxLocked': string;
  'examples.tagSlideDeck': string;
  'examples.tagTemplate': string;
  'examples.tagDesignSystem': string;
  'examples.tagMobilePrototype': string;
  'examples.tagDesktopPrototype': string;
  'examples.tagImage': string;
  'examples.tagVideo': string;
  'examples.tagAudio': string;
  'examples.previewLabel': string;

  // Design systems tab
  'ds.surfaceLabel': string;
  'ds.surfaceWeb': string;
  'ds.surfaceImage': string;
  'ds.surfaceVideo': string;
  'ds.surfaceAudio': string;
  'ds.searchPlaceholder': string;
  'ds.emptyNoMatch': string;
  'ds.badgeDefault': string;
  'ds.preview': string;
  'ds.kitVisualize': string;
  'ds.kitEdit': string;
  'ds.kitSource': string;
  'ds.saveDesignMd': string;
  'ds.saving': string;
  'ds.copyDesignMd': string;
  'ds.editDesignMd': string;
  'ds.openDesignMd': string;
  'ds.copyDesignMdModule': string;
  'ds.editDesignMdModule': string;
  'ds.openDesignMdModule': string;
  'ds.uploadMd': string;
  'ds.uploadFont': string;
  'ds.pasteImage': string;
  'ds.refresh': string;
  'ds.download': string;
  'ds.importFolder': string;
  'ds.reset': string;
  'ds.editingDesignMdHint': string;
  'ds.editingModuleHint': string;
  'ds.manualEditModuleHint': string;
  'ds.saveModule': string;
  'ds.uploadLogo': string;
  'ds.uploadImage': string;
  'ds.uploading': string;
  'ds.moduleEmptyLogo': string;
  'ds.moduleEmptyFonts': string;
  'ds.actionDone': string;
  'ds.actionFailed': string;
  'ds.uploadDone': string;
  'ds.uploadFailed': string;
  'ds.deleteLogo': string;
  'ds.deleteImage': string;
  'ds.deleteProjectConfirm': string;
  'ds.deleteProjectAction': string;
  'ds.editColor': string;
  'ds.colorLabel': string;
  'ds.hexLabel': string;
  'ds.hexValueLabel': string;
  'ds.saveColor': string;
  'ds.invalidHexColor': string;
  'ds.colorSaveFailed': string;
  'ds.colorResetFailed': string;
  'ds.designMdSectionLabel': string;
  'ds.noOriginalColor': string;
  'ds.manifestReadFailed': string;
  'ds.reviewCollapseSection': string;
  'ds.reviewExpandSection': string;
  'ds.reviewActionsLabel': string;
  'ds.reviewNeedsReview': string;
  'ds.reviewLooksGood': string;
  'ds.reviewNeedsWork': string;
  'ds.reviewNeedsWorkEllipsis': string;
  'ds.reviewEditFile': string;
  'ds.reviewFeedbackLabel': string;
  'ds.reviewFeedbackPlaceholder': string;
  'ds.reviewChangedAfterFeedback': string;
  'ds.reviewChangedDuringRun': string;
  'ds.reviewLastFeedback': string;
  'ds.reviewLastReviewed': string;
  'ds.previewGenerating': string;
  'ds.creatingProjectTitle': string;
  'ds.creatingProjectSubtitle': string;
  'ds.generationProgressLabel': string;
  'ds.publishRepoRequiredTitle': string;
  'ds.publishDesignSystem': string;
  'ds.unpublishDesignSystem': string;
  'ds.publish': string;
  'ds.published': string;
  'ds.extractionRunningTitle': string;
  'ds.extractionCompleteTitle': string;
  'ds.extractionRunningBody': string;
  'ds.extractionCompleteBody': string;
  'ds.publishCardPublished': string;
  'ds.publishCardDraft': string;
  'ds.useSystemTitle': string;
  'ds.useSystemBody': string;
  'ds.createNewDesign': string;
  'ds.openSourceContext': string;
  'ds.manifestNeedsAttention': string;
  'ds.openManifest': string;
  'ds.workspacePreparing': string;
  'ds.workspaceLoadingLabel': string;
  'ds.sectionQueued': string;
  'ds.sectionReviewUpdatedFiles': string;
  'ds.sectionRequiredFileMissing': string;
  'ds.sectionNoFilesYet': string;
  'ds.generationSourceTitle': string;
  'ds.generationSourceDetail': string;
  'ds.generationGuidanceTitle': string;
  'ds.generationGuidanceDetail': string;
  'ds.generationTokensTitle': string;
  'ds.generationTokensDetail': string;
  'ds.generationPreviewsTitle': string;
  'ds.generationPreviewsDetail': string;
  'ds.generationUiKitTitle': string;
  'ds.generationUiKitDetail': string;
  'ds.generationAssetsTitle': string;
  'ds.generationAssetsDetail': string;
  'ds.phaseQueuedTypography': string;
  'ds.phaseQueuedTokens': string;
  'ds.phaseQueuedSpacing': string;
  'ds.phaseQueuedUiKit': string;
  'ds.phaseQueuedAssets': string;
  'ds.phaseReadingTypography': string;
  'ds.phaseReadingTokens': string;
  'ds.phaseReadingSpacing': string;
  'ds.phaseReadingUiKit': string;
  'ds.phaseReadingAssets': string;
  'ds.phaseWritingTypography': string;
  'ds.phaseWritingTokens': string;
  'ds.phaseWritingSpacing': string;
  'ds.phaseBuildingUiKit': string;
  'ds.phaseUpdatingAssets': string;
  'ds.phaseNeedsAttention': string;
  'ds.phaseUpdated': string;
  'ds.sectionActivityFromTodo': string;
  'ds.sectionActivityReadFiles': string;
  'ds.sectionActivityPhaseFiles': string;
  'ds.sectionRunningReadingContext': string;
  'ds.sectionRunningNow': string;
  'ds.agentFeedbackQueued': string;
  'ds.agentFeedbackSent': string;
  'ds.agentFeedbackSentAt': string;
  'ds.agentFeedbackFailedWithError': string;
  'ds.agentFeedbackFailed': string;
  'ds.agentFeedbackUnknown': string;
  'ds.repoConnectBannerTitle': string;
  'ds.repoConnectCardTitle': string;
  'ds.repoConnectPendingBody': string;
  'ds.repoConnectPendingButton': string;
  'ds.repoConnectedTitle': string;
  'ds.repoConnectedBody': string;
  'ds.repoImportButton': string;
  'ds.repoConnectBannerBody': string;
  'ds.repoConnectCardBody': string;
  'ds.repoConnectButton': string;
  'ds.kitErrorTitle': string;
  'ds.kitErrorRetry': string;
  'ds.shortcutsLabel': string;
  'ds.shortcutsHint': string;
  'ds.moduleEmptyImages': string;
  'ds.previewTitle': string;
  'ds.categoryAll': string;
  'ds.categoryUncategorized': string;
  'ds.showcase': string;
  'ds.tokens': string;
  'ds.specToggle': string;
  'ds.specLoading': string;

  // Avatar menu (project topbar)
  'avatar.title': string;
  'avatar.localCli': string;
  'avatar.anthropicApi': string;
  'avatar.useLocal': string;
  'avatar.useApi': string;
  'avatar.codeAgent': string;
  'avatar.rescan': string;
  'avatar.amrConsole': string;
  'avatar.amrConsoleMeta': string;
  'avatar.settings': string;
  'avatar.backToProjects': string;
  'avatar.metaActive': string;
  'avatar.metaOffline': string;
  'avatar.metaSelected': string;
  'avatar.noAgentSelected': string;
  'avatar.modelSection': string;
  'avatar.modelLabel': string;
  'avatar.reasoningLabel': string;
  'avatar.customSuffix': string;
  'socialShare.openDesignSection': string;
  'socialShare.openDesignTitle': string;
  'socialShare.openDesignText': string;
  'socialShare.openDesignCopyText': string;
  'socialShare.projectSection': string;
  'socialShare.publishPageTitle': string;
  'socialShare.publishPageSubtitle': string;
  'socialShare.projectTitle': string;
  'socialShare.projectText': string;
  'socialShare.projectCopyText': string;
  'socialShare.deployFirst': string;
  'socialShare.copied': string;
  'socialShare.platform.x': string;
  'socialShare.platform.linkedin': string;
  'socialShare.platform.facebook': string;
  'socialShare.platform.reddit': string;
  'socialShare.platform.telegram': string;
  'socialShare.platform.whatsapp': string;
  'socialShare.platform.weibo': string;
  'socialShare.platform.line': string;
  'socialShare.platform.instagram': string;
  'socialShare.platform.xiaohongshu': string;

  // Inline CLI / model switcher (entry topbar)
  'inlineSwitcher.chipTitle': string;
  'inlineSwitcher.chipCli': string;
  'inlineSwitcher.chipByok': string;
  'inlineSwitcher.modelDefault': string;
  'inlineSwitcher.noAgent': string;
  'inlineSwitcher.modeLabel': string;
  'inlineSwitcher.agentLabel': string;
  'inlineSwitcher.providerLabel': string;
  'inlineSwitcher.modelLabel': string;
  'inlineSwitcher.useCli': string;
  'inlineSwitcher.useByok': string;
  'inlineSwitcher.daemonOffline': string;
  'inlineSwitcher.noAgentsDetected': string;
  'inlineSwitcher.openSettingsForModel': string;
  'inlineSwitcher.missingApiKey': string;
  'inlineSwitcher.openFullSettings': string;
  'inlineSwitcher.customSuffix': string;

  // Project view / chat pane / composer
  'project.backToProjects': string;
  'project.brandReadyTitle': string;
  'project.brandReadyTitleGeneric': string;
  'project.brandReadyCta': string;
  'project.brandReadyDismiss': string;
  'project.brandReadyRefineHint': string;
  'project.brandReadyAiOptimize': string;
  'project.brandReadyEditManually': string;
  'project.brandReadyPreviewOpened': string;
  'project.metaFreeform': string;
  'project.customInstructions': string;
  'project.customInstructionsPlaceholder': string;
  'project.instructionsActive': string;
  'project.missing': string;
  'project.resizeChatPanel': string;
  'chat.tabChat': string;
  'chat.amrCard.switchTitle': string;
  'chat.amrCard.switchBody': string;
  'chat.amrCard.chipOfficial': string;
  'chat.amrCard.chipNoKey': string;
  'chat.amrCard.chipAutoRetry': string;
  'chat.amrCard.switchCta': string;
  'chat.amrError.authMessage': string;
  'chat.amrError.balanceMessage': string;
  'chat.amrError.authorizeCta': string;
  'chat.amrError.rechargeCta': string;
  'chat.antigravityError.launchTerminalCta': string;
  'chat.antigravityError.launchSwitchModelCta': string;
  'chat.connectionDropped': string;
  'chat.runError.title.authRequired': string;
  'chat.runError.title.balance': string;
  'chat.runError.title.connectionDropped': string;
  'chat.runError.title.signInRequired': string;
  'chat.runError.title.rateLimited': string;
  'chat.runError.title.generic': string;
  'chat.runError.signInMessage.amr': string;
  'chat.runError.signInMessage.other': string;
  'chat.runError.agentFallback': string;
  'chat.runError.sourceLabel': string;
  'chat.runError.sourceExpandAria': string;
  'chat.runError.sourceCollapseAria': string;
  'chat.tabComments': string;
  'chat.commentsSoon': string;
  'chat.comments.attached': string;
  'chat.comments.emptyAttached': string;
  'chat.comments.saved': string;
  'chat.comments.emptySaved': string;
  'chat.comments.add': string;
  'chat.comments.addAll': string;
  'chat.comments.remove': string;
  'chat.comments.placeholder': string;
  'chat.comments.addSend': string;
  'chat.comments.updateSend': string;
  'chat.comments.removeAttachment': string;
  'chat.comments.removeAttachmentAria': string;
  'chat.comments.comment': string;
  'chat.comments.sendToChat': string;
  'chat.comments.sending': string;
  'chat.comments.edit': string;
  'chat.comments.select': string;
  'chat.comments.selectAll': string;
  'chat.comments.deselect': string;
  'chat.comments.nSelected': string;
  'chat.comments.pin': string;
  'chat.comments.addNote': string;
  'chat.comments.savedToast': string;
  'chat.comments.pinSavedToast': string;
  'chat.comments.pinAtCoords': string;
  'chat.comments.capturedItems': string;
  'chat.comments.clear': string;
  'chat.comments.targetImage': string;
  'chat.comments.targetControl': string;
  'chat.comments.targetLink': string;
  'chat.comments.targetText': string;
  'chat.comments.targetSection': string;
  'chat.comments.targetPage': string;
  'chat.comments.targetArea': string;
  'chat.annotationNotePlaceholder': string;
  'chat.annotationAttachImage': string;
  'chat.annotationAttachedImages': string;
  'chat.annotationAttachedRemove': string;
  'chat.annotationAddToInput': string;
  'chat.annotationAddingToInput': string;
  'chat.annotationQueue': string;
  'chat.annotationQueueing': string;
  'chat.annotationSending': string;
  'chat.annotationSendDisabledReason': string;
  'chat.annotationPreviewMissing': string;
  'chat.annotationPreviewMissingInk': string;
  'chat.annotationSentWithoutScreenshot': string;
  'chat.annotationTimeout': string;
  'chat.annotationFailed': string;
  'chat.annotationProjectCreateFailed': string;
  'chat.annotationUploadFailed': string;
  'chat.inspect.noEditableTargets': string;
  'chat.inspect.noCommentTargets': string;
  'chat.inspect.editHint': string;
  'chat.inspect.commentHint': string;
  'chat.conversationsTitle': string;
  'chat.conversationsAria': string;
  'chat.newConversation': string;
  'chat.newConversationsTitle': string;
  'chat.conversationsHeading': string;
  'chat.new': string;
  'chat.emptyConversations': string;
  'chat.deleteConversation': string;
  'chat.renameConversationLabel': string;
  'chat.deleteConversationConfirm': string;
  'chat.untitledConversation': string;
  'chat.forkedConversationTitle': string;
  'chat.forkConversationFailed': string;
  'chat.startTitle': string;
  'chat.startHint': string;
  'chat.brandBrowserAssistDesktopOnly': string;
  'chat.brandBrowserAssistReadFailed': string;
  'chat.brandBrowserAssistMessage': string;
  'brandEnrichment.title': string;
  'brandEnrichment.body': string;
  'brandEnrichment.cta': string;
  'brandEnrichment.busy': string;
  'brandExtractionTranscript.sourceDesignMd': string;
  'brandExtractionTranscript.user': string;
  'brandExtractionTranscript.doneTitle': string;
  'brandExtractionTranscript.doneBody': string;
  'brandExtractionTranscript.next': string;
  'chat.fillInputTitle': string;
  'chat.jumpToLatest': string;
  'chat.scrollToLatest': string;
  'chat.you': string;
  'chat.openFile': string;
  'chat.copyPrompt': string;
  'chat.copyErrorDiagnostic': string;
  'chat.copyDone': string;
  'chat.composerPlaceholder': string;
  'chat.activeFileEditingLabel': string;
  'chat.activeFilePlaceholder': string;
  'chat.designArtifactsShowMore': string;
  'chat.mode.chat.label': string;
  'chat.mode.chat.title': string;
  'chat.mode.chat.summary': string;
  'chat.mode.chat.solves': string;
  'chat.mode.chat.query1': string;
  'chat.mode.chat.query2': string;
  'chat.mode.chat.query3': string;
  'chat.mode.design.label': string;
  'chat.mode.design.title': string;
  'chat.mode.design.summary': string;
  'chat.mode.design.solves': string;
  'chat.mode.design.query1': string;
  'chat.mode.design.query2': string;
  'chat.mode.design.query3': string;
  'chat.mode.cardBestFor': string;
  'chat.mode.cardTry': string;
  'chat.composerHint': string;
  'chat.cliSettingsTitle': string;
  'chat.cliSettingsAria': string;
  'chat.attachTitle': string;
  'chat.addContextTitle': string;
  'chat.attachAria': string;
  'chat.importTitle': string;
  'chat.importLabel': string;
  'chat.importComingSoon': string;
  'chat.importSoon': string;
  'chat.importFig': string;
  'chat.importGitHub': string;
  'chat.importWeb': string;
  'chat.importFolder': string;
  'chat.importSkills': string;
  'chat.importProject': string;
  'chat.importDesignSystemHeader': string;
  'chat.importDesignSystemBack': string;
  'chat.importDesignSystemSearch': string;
  'chat.importDesignSystemEmpty': string;
  'chat.importDesignSystemNone': string;
  'chat.importDesignSystemNoneSub': string;
  'chat.importDesignSystemActive': string;
  'chat.importDesignSystemSwitched': string;
  'chat.importDesignSystemFailed': string;
  'chat.importDesignSystemLoadFailed': string;
  'browserUse.title': string;
  'browserUse.summary': string;
  'browserUse.searchAria': string;
  'browserUse.searchPlaceholder': string;
  'browserUse.empty': string;
  'browserUse.unavailable': string;
  'browserUse.added': string;
  'browserUse.category.assets': string;
  'browserUse.category.tokens': string;
  'browserUse.category.motion': string;
  'browserUse.category.visual': string;
  'browserUse.category.structure': string;
  'browserUse.category.project': string;
  'browserUse.category.general': string;
  'browserUse.input.none': string;
  'browserUse.input.assetKind': string;
  'browserUse.input.optionalSelector': string;
  'browserUse.input.requirementSelector': string;
  'browserUse.input.selectorOptional': string;
  'browserUse.input.scaleOne': string;
  'browserUse.input.selectorScaleTwo': string;
  'browserUse.input.gridOverlay': string;
  'browserUse.input.bodySelector': string;
  'browserUse.input.navigate': string;
  'browserUse.input.selector': string;
  'browserUse.input.selectorText': string;
  'browserUse.input.scroll': string;
  'browserUse.input.terminalRun': string;
  'browserUse.input.command': string;
  'browserUse.input.maxChars': string;
  'browserUse.input.custom': string;
  'browserUse.action.extract_logo.output': string;
  'browserUse.action.list_images.output': string;
  'browserUse.action.download_assets.output': string;
  'browserUse.action.extract_svgs.output': string;
  'browserUse.action.optimize_svgs.output': string;
  'browserUse.action.extract_colors.output': string;
  'browserUse.action.extract_fonts.output': string;
  'browserUse.action.extract_design_tokens.output': string;
  'browserUse.action.extract_type_scale.output': string;
  'browserUse.action.extract_buttons.output': string;
  'browserUse.action.extract_grid_system.output': string;
  'browserUse.action.extract_breakpoints.output': string;
  'browserUse.action.extract_gradients.output': string;
  'browserUse.action.extract_shadows.output': string;
  'browserUse.action.extract_easings.output': string;
  'browserUse.action.export_tokens.output': string;
  'browserUse.action.extract_animations.output': string;
  'browserUse.action.validate_view.output': string;
  'browserUse.action.audit_layout.output': string;
  'browserUse.action.audit_accessibility.output': string;
  'browserUse.action.responsive_screenshots.output': string;
  'browserUse.action.screenshot_full.output': string;
  'browserUse.action.screenshot_element.output': string;
  'browserUse.action.screenshot_with_grid.output': string;
  'browserUse.action.screenshot_dark_mode.output': string;
  'browserUse.action.generate_styleguide.output': string;
  'browserUse.action.extract_html.output': string;
  'browserUse.action.extract_component_inventory.output': string;
  'browserUse.action.extract_copy.output': string;
  'browserUse.action.extract_nav.output': string;
  'browserUse.action.extract_forms.output': string;
  'browserUse.action.run_project.output': string;
  'browserUse.action.detect_project.output': string;
  'browserUse.action.page_info.output': string;
  'browserUse.action.snapshot.output': string;
  'browserUse.action.navigate.output': string;
  'browserUse.action.click.output': string;
  'browserUse.action.type_text.output': string;
  'browserUse.action.scroll.output': string;
  'browserUse.action.extract_og_metadata.output': string;
  'browserUse.action.terminal_run.output': string;
  'browserUse.action.terminal_run_background.output': string;
  'browserUse.action.terminal_read.output': string;
  "chat.designToolbox.title": string;
  "chat.designToolbox.tooltip": string;
  "chat.designToolbox.aria": string;
  "chat.designToolbox.searchPlaceholder": string;
  "chat.designToolbox.searchAria": string;
  "chat.designToolbox.followupSection": string;
  "chat.designToolbox.resourcesSection": string;
  "chat.designToolbox.selected": string;
  "chat.designToolbox.noResources": string;
  "chat.designToolbox.badge.video": string;
  "chat.designToolbox.badge.image": string;
  "chat.designToolbox.badge.motion": string;
  "chat.designToolbox.badge.polish": string;
  "chat.designToolbox.action.auto-match.title": string;
  "chat.designToolbox.action.auto-match.badge": string;
  "chat.designToolbox.action.auto-match.description": string;
  "chat.designToolbox.action.motion.title": string;
  "chat.designToolbox.action.motion.badge": string;
  "chat.designToolbox.action.motion.description": string;
  "chat.designToolbox.action.motion-polish.title": string;
  "chat.designToolbox.action.motion-polish.badge": string;
  "chat.designToolbox.action.motion-polish.description": string;
  "chat.designToolbox.action.anti-ai-polish.title": string;
  "chat.designToolbox.action.anti-ai-polish.badge": string;
  "chat.designToolbox.action.anti-ai-polish.description": string;
  "chat.designToolbox.action.visual-polish.title": string;
  "chat.designToolbox.action.visual-polish.badge": string;
  "chat.designToolbox.action.visual-polish.description": string;
  "chat.designToolbox.action.image-gen.title": string;
  "chat.designToolbox.action.image-gen.badge": string;
  "chat.designToolbox.action.image-gen.description": string;
  "chat.designToolbox.action.video-gen.title": string;
  "chat.designToolbox.action.video-gen.badge": string;
  "chat.designToolbox.action.video-gen.description": string;
  "chat.designToolbox.kind.skill": string;
  "chat.designToolbox.kind.plugin": string;
  "chat.designToolbox.kind.mcp": string;
  "chat.designToolbox.kind.mcpTemplate": string;
  "chat.designToolbox.kind.connector": string;
  "chat.designToolbox.kind.designFile": string;
  "chat.designToolbox.context.browser": string;
  "chat.designToolbox.context.designFiles": string;
  "chat.designToolbox.context.designSystem": string;
  "chat.designToolbox.context.folder": string;
  "chat.designToolbox.context.terminal": string;
  "chat.designToolbox.context.sideChat": string;
  "chat.designToolbox.context.liveArtifact": string;
  "chat.designToolbox.context.file": string;
  "chat.designToolbox.prompt.contextGeneric": string;
  "chat.designToolbox.prompt.contextSpecific": string;
  "chat.designToolbox.prompt.preserveDraft": string;
  "chat.designToolbox.prompt.selectedSkill": string;
  "chat.designToolbox.prompt.noSkill": string;
  "chat.designToolbox.prompt.autoMatchIntro": string;
  "chat.designToolbox.prompt.autoMatchStep1": string;
  "chat.designToolbox.prompt.autoMatchStep2": string;
  "chat.designToolbox.prompt.autoMatchStep3": string;
  "chat.designToolbox.prompt.autoMatchStep4": string;
  "chat.designToolbox.prompt.motion": string;
  "chat.designToolbox.prompt.motionPolish": string;
  "chat.designToolbox.prompt.antiAiPolish": string;
  "chat.designToolbox.prompt.visualPolish": string;
  "chat.designToolbox.prompt.imageGen": string;
  "chat.designToolbox.prompt.videoGen": string;
  "chat.designToolbox.prompt.useSkill": string;
  "chat.designToolbox.prompt.skillInstruction": string;
  "chat.designToolbox.prompt.selectedResource": string;
  "chat.designToolbox.prompt.resourceDescription": string;
  "chat.designToolbox.prompt.pluginResource": string;
  "chat.designToolbox.prompt.mcpResource": string;
  "chat.designToolbox.prompt.mcpTemplateResource": string;
  "chat.designToolbox.prompt.connectorResource": string;
  "chat.designToolbox.prompt.fileResource": string;
  "chat.designToolbox.prompt.resourceIndex": string;
  "chat.designToolbox.prompt.searchableSkills": string;
  "chat.designToolbox.prompt.searchablePlugins": string;
  "chat.designToolbox.prompt.availableMcp": string;
  "chat.designToolbox.prompt.connectedConnectors": string;
  "chat.designToolbox.prompt.referenceDesignFiles": string;
  "chat.designToolbox.prompt.mcpTemplateName": string;
  "chat.designToolbox.prompt.processRule": string;
  "chat.designToolbox.prompt.moreSuffix": string;
  "chat.designToolbox.prompt.compactLine": string;
  'chat.mentionTabsAria': string;
  'chat.mentionTabAll': string;
  'chat.mentionTabTabs': string;
  'chat.mentionTabPlugins': string;
  'chat.mentionTabSkills': string;
  'chat.mentionTabMcp': string;
  'chat.mentionTabConnectors': string;
  'chat.mentionTabFiles': string;
  'chat.mentionNoResults': string;
  'chat.mentionSearchPrompt': string;
  'chat.mentionSectionTabs': string;
  'chat.mentionSectionPlugins': string;
  'chat.mentionSectionSkills': string;
  'chat.mentionSectionMcp': string;
  'chat.mentionSectionConnectors': string;
  'chat.mentionSectionFiles': string;
  'chat.mentionActiveSkill': string;
  'chat.mentionUseMcpTitle': string;
  'chat.mentionUseConnectorTitle': string;
  'chat.mentionPluginOfficial': string;
  'chat.mentionPluginMine': string;
  'chat.linkedFolderRemoveAria': string;
  'chat.linkedFolderNotFound': string;
  'chat.linkedFolderAlready': string;
  'chat.linkedFolderPickError': string;
  'chat.queuedHeader': string;
  'chat.queuedToSend': string;
  'chat.queuedEditQueuedTaskAria': string;
  'chat.queuedSave': string;
  'chat.queuedCancel': string;
  'chat.queuedReorder': string;
  'chat.queuedEdit': string;
  'chat.queuedMore': string;
  'chat.queuedFollowUpFallback': string;
  'chat.send': string;
  'chat.stop': string;
  'chat.removeAria': string;
  'chat.example1Title': string;
  'chat.example1Tag': string;
  'chat.example1Prompt': string;
  'chat.example2Title': string;
  'chat.example2Tag': string;
  'chat.example2Prompt': string;
  'chat.example3Title': string;
  'chat.example3Tag': string;
  'chat.example3Prompt': string;

  // Preview modal
  'preview.usePlugin': string;
  'preview.usePluginOnly': string;
  'preview.usePluginOnlyDesc': string;
  'preview.replicateContent': string;
  'preview.replicateContentDesc': string;
  'preview.duplicateTemplate': string;
  'preview.duplicateTemplateDesc': string;
  'preview.shareMenu': string;
  'preview.exportMenu': string;
  'preview.shareTemplateBadge': string;
  'preview.shareToX': string;
  'preview.shareToReddit': string;
  'preview.shareToFacebook': string;
  'preview.shareToLinkedIn': string;
  'preview.shareToInstagram': string;
  'preview.shareToXiaohongshu': string;
  'preview.copyTemplateLink': string;
  'preview.copyShareText': string;
  'preview.shareSocialGroup': string;
  'preview.shareCopyGroup': string;
  'preview.shareExportGroup': string;
  'preview.shareCopied': string;
  'preview.shareCopyFailed': string;
  'preview.shareTextDefault': string;
  'preview.openInNewTab': string;
  'preview.exit': string;
  'preview.fullscreen': string;
  'preview.closeTitle': string;
  'preview.loading': string;
  'preview.errorTitle': string;
  'preview.errorBody': string;
  'preview.retry': string;
  // Friendly placeholder copy for surfaces whose `od.preview.type` is
  // not `html`, or whose manifest declares a preview entry that doesn't
  // ship on disk — they have no fetchable example artifact, so the
  // loading / error states are misleading. Issues #897, #2840, #3216.
  // Body uses the `{kind}` placeholder (raw `od.preview.type` token,
  // e.g. "markdown" or "image"); both keys use the `{noun}` placeholder
  // so the same wording reads correctly on skills, plugins, and design
  // templates (filled from one of the `preview.noun.*` keys below).
  'preview.unavailableTitle': string;
  'preview.unavailableBody': string;
  // Noun variants so the unavailable placeholder reads with the right
  // word for each surface — Skills tab vs. Community/Plugins vs. deck
  // design-templates. Keep these short, capitalised by the host
  // language's conventions, and translatable in every locale.
  'preview.nounSkill': string;
  'preview.nounPlugin': string;
  'preview.nounTemplate': string;
  'preview.showSidebar': string;
  'preview.hideSidebar': string;

  // Misc fallback names
  'misc.savedTemplate': string;
  'misc.primary': string;
  'misc.designSystem': string;

  // Workspace / file viewer / design files panel
  'workspace.designFiles': string;
  'workspace.focusMode': string;
  'workspace.showChat': string;
  'workspace.closeTab': string;
  'workspace.deleteFileConfirm': string;
  'workspace.deleteSelectedFilesConfirm': string;
  'workspace.deleteSelectedFilesPartial': string;
  'workspace.openFromDesignFiles': string;
  'workspace.designFilesLink': string;
  'workspace.loadingSketch': string;
  'workspace.newTab': string;
  'workspace.searchFilesPlaceholder': string;
  'workspace.openTabs': string;
  'workspace.openFile': string;
  'workspace.noFilesMatch': string;
  'workspace.allFiles': string;
  'workspace.createNew': string;
  'workspace.tabOpen': string;
  'workspace.sideChatContextBanner': string;
  'workspace.sideChatDefaultTitle': string;
  'workspace.newTerminal': string;
  'workspace.newTerminalDescription': string;
  'workspace.newBrowser': string;
  'workspace.newBrowserDescription': string;
  'workspace.terminalStarting': string;
  'workspace.terminalStartingDescription': string;
  'workspace.terminalSessionEnded': string;
  'workspace.terminalReconnecting': string;
  'workspace.terminalStartFailed': string;
  'workspace.terminalRestart': string;
  'designFiles.title': string;
  'designFiles.upload': string;
  'designFiles.pasteText': string;
  'designFiles.newSketch': string;
  'designFiles.empty': string;
  'designFiles.refresh': string;
  'designFiles.delete': string;
  'designFiles.searchPlaceholder': string;
  'designFiles.up': string;
  'designFiles.back': string;
  'designFiles.crumbs': string;
  'designFiles.rowMenu': string;
  'designFiles.openInTab': string;
  'designFiles.download': string;
  'designFiles.downloadSelected': string;
  'designFiles.deleteSelected': string;
  'designFiles.clearSelection': string;
  'designFiles.selectPage': string;
  'designFiles.selectAll': string;
  'designFiles.dropTitle': string;
  'designFiles.dropDesc': string;
  'designFiles.dropLabel': string;
  'designFiles.upload.title': string;
  'designFiles.paste.title': string;
  'designFiles.upload.label': string;
  'designFiles.paste.label': string;
  'designFiles.previewOpen': string;
  'designFiles.previewClose': string;
  'designFiles.modified': string;
  'designFiles.weeksAgo': string;
  'designFiles.groupBy': string;
  'designFiles.groupByKind': string;
  'designFiles.groupByModified': string;
  'designFiles.filterBy': string;
  'designFiles.filterClear': string;
  'designFiles.filterCount': string;
  'designFiles.expandGroup': string;
  'designFiles.collapseGroup': string;
  'designFiles.sectionPages': string;
  'designFiles.sectionScripts': string;
  'designFiles.sectionImages': string;
  'designFiles.sectionSketches': string;
  'designFiles.sectionLiveArtifacts': string;
  'designFiles.sectionOther': string;
  'designFiles.modifiedToday': string;
  'designFiles.modifiedYesterday': string;
  'designFiles.modifiedPrevious7Days': string;
  'designFiles.modifiedPrevious30Days': string;
  'designFiles.modifiedOlder': string;
  'designFiles.showMore': string;
  'designFiles.kindHtml': string;
  'designFiles.kindImage': string;
  'designFiles.kindSketch': string;
  'designFiles.kindText': string;
  'designFiles.kindCode': string;
  'designFiles.kindPdf': string;
  'designFiles.kindDocument': string;
  'designFiles.kindPresentation': string;
  'designFiles.kindSpreadsheet': string;
  'designFiles.kindLiveArtifact': string;
  'designFiles.kindBinary': string;
  'designFiles.kindFolder': string;
  'designFiles.folderCount': string;
  'designFiles.newFolderTitle': string;
  'designFiles.newFolderLabel': string;
  'designFiles.moveTitle': string;
  'designFiles.moveLabel': string;
  'designFiles.moveRoot': string;
  'designFiles.moveNewFolder': string;
  'designFiles.move': string;
  'designFiles.copyPath': string;
  'designFiles.copiedPath': string;
  'designFiles.deleteFolderConfirm': string;
  'designFiles.folderCreated': string;
  'designFiles.folderDeleted': string;
  'designFiles.filesMoved': string;
  'designFiles.colName': string;
  'designFiles.colKind': string;
  'designFiles.colModified': string;
  'designFiles.perPage': string;
  'designFiles.all': string;
  'designFiles.prev': string;
  'designFiles.next': string;
  'designFiles.jumpToPage': string;
  'designFiles.pageInfo': string;
  'designFiles.sectionFolders': string;
  'designFiles.sectionStylesheets': string;
  'designFiles.sectionDocuments': string;
  'designFiles.kindStylesheet': string;
  'designFiles.modifiedExt': string;
  'designFiles.usefulInfoLabel': string;
  'designFiles.usefulInfoTip': string;
  'designFiles.usefulInfoTip2': string;
  'designFiles.usefulInfoTip4': string;
  'designFiles.usefulInfoTip5': string;
  'designFiles.usefulInfoTip6': string;
  'designFiles.usefulInfoTip7': string;
  'designFiles.usefulInfoTip8': string;
  'designFiles.usefulInfoTip9': string;
  'designFiles.usefulInfoTip10': string;
  'designFiles.usefulInfoTip11': string;
  'designFiles.usefulInfoTip12': string;
  'designFiles.usefulInfoTip13': string;
  'designFiles.usefulInfoTip14': string;
  'designFiles.usefulInfoTip15': string;
  'quickSwitcher.placeholder': string;
  'quickSwitcher.empty': string;
  'quickSwitcher.noMatches': string;
  'quickSwitcher.navigate': string;
  'quickSwitcher.open': string;
  'quickSwitcher.close': string;
  'pasteDialog.title': string;
  'pasteDialog.hint': string;
  'pasteDialog.fileNameLabel': string;
  'pasteDialog.namePlaceholder': string;
  'pasteDialog.contentLabel': string;
  'pasteDialog.contentPlaceholder': string;
  'pasteDialog.save': string;
  'pasteDialog.cancel': string;
  'sketch.save': string;
  'sketch.cancel': string;
  'sketch.saving': string;
  'sketch.saved': string;
  'sketch.tooltipDirty': string;
  'sketch.tooltipClean': string;
  'fileViewer.empty': string;
  'fileViewer.loading': string;
  'fileViewer.exportPptx': string;
  'fileViewer.openInNewTab': string;
  'fileViewer.copyPath': string;
  'fileViewer.copied': string;
  'fileViewer.share': string;
  'fileViewer.binaryMeta': string;
  'fileViewer.binaryNote': string;
  'fileViewer.pdfMeta': string;
  'fileViewer.documentMeta': string;
  'fileViewer.presentationMeta': string;
  'fileViewer.spreadsheetMeta': string;
  'fileViewer.previewUnavailable': string;
  'fileViewer.download': string;
  'fileViewer.open': string;
  'fileViewer.imageMeta': string;
  'fileViewer.reactMeta': string;
  'fileViewer.sketchMeta': string;
  'fileViewer.markdownStreamingMeta': string;
  'fileViewer.markdownErrorMeta': string;
  'fileViewer.markdownStreamingStatus': string;
  'fileViewer.markdownErrorStatus': string;
  'fileViewer.videoMeta': string;
  'fileViewer.audioMeta': string;
  'fileViewer.reload': string;
  'fileViewer.reloadDisk': string;
  'fileViewer.copy': string;
  'fileViewer.copyTitle': string;
  'fileViewer.saveDisabled': string;
  'fileViewer.save': string;
  'fileViewer.preview': string;
  'fileViewer.source': string;
  'fileViewer.tweaks': string;
  'fileViewer.tweaksUnavailable': string;
  'fileViewer.jsxModuleTitle': string;
  'fileViewer.jsxModuleBody': string;
  'fileViewer.jsxModuleCta': string;
  'fileViewer.comment': string;
  'fileViewer.edit': string;
  'fileViewer.draw': string;
  'fileViewer.mark': string;
  'fileViewer.markTool': string;
  'fileViewer.boxSelect': string;
  'fileViewer.screenshot': string;
  'manualEdit.layers': string;
  'manualEdit.editableCount': string;
  'manualEdit.hiddenBadge': string;
  'manualEdit.title': string;
  'manualEdit.fallbackTitle': string;
  'manualEdit.movePanel': string;
  'manualEdit.editParams': string;
  'manualEdit.closePanel': string;
  'manualEdit.selectLayer': string;
  'manualEdit.empty': string;
  'manualEdit.noEditableLayers': string;
  'manualEdit.noClass': string;
  'manualEdit.tabsAria': string;
  'manualEdit.tabContent': string;
  'manualEdit.tabStyle': string;
  'manualEdit.tabAttributes': string;
  'manualEdit.tabHtml': string;
  'manualEdit.tabSource': string;
  'manualEdit.attributesJson': string;
  'manualEdit.selectedHtml': string;
  'manualEdit.fullSource': string;
  'manualEdit.applyContent': string;
  'manualEdit.applyStyle': string;
  'manualEdit.applyAttributes': string;
  'manualEdit.applyHtml': string;
  'manualEdit.applySource': string;
  'manualEdit.invalidAttributes': string;
  'manualEdit.changes': string;
  'manualEdit.undo': string;
  'manualEdit.redo': string;
  'manualEdit.noChanges': string;
  'manualEdit.imageUrl': string;
  'manualEdit.altText': string;
  'manualEdit.label': string;
  'manualEdit.text': string;
  'manualEdit.href': string;
  'manualEdit.textColor': string;
  'manualEdit.background': string;
  'manualEdit.fontSize': string;
  'manualEdit.weight': string;
  'manualEdit.align': string;
  'manualEdit.padding': string;
  'manualEdit.margin': string;
  'manualEdit.radius': string;
  'manualEdit.border': string;
  'manualEdit.width': string;
  'manualEdit.minHeight': string;
  'manualEdit.deleteElement': string;
  'manualEdit.deleteElementConfirm': string;
  'manualEdit.uploadImage': string;
  'manualEdit.uploadingImage': string;
  'manualEdit.uploadImageFailed': string;
  'manualEdit.focusSlides': string;
  'manualEdit.showPanels': string;
  'fileViewer.zoomOut': string;
  'fileViewer.zoomIn': string;
  'fileViewer.resetZoom': string;
  'fileViewer.viewportAria': string;
  'fileViewer.viewportDesktop': string;
  'fileViewer.viewportDesktopTitle': string;
  'fileViewer.viewportTablet': string;
  'fileViewer.viewportTabletTitle': string;
  'fileViewer.viewportMobile': string;
  'fileViewer.viewportMobileTitle': string;
  'fileViewer.reloadAria': string;
  'fileViewer.previousSlide': string;
  'fileViewer.nextSlide': string;
  'fileViewer.slideNavAria': string;
  'fileViewer.present': string;
  'fileViewer.presentInTab': string;
  'fileViewer.presentFullscreen': string;
  'fileViewer.presentNewTab': string;
  'fileViewer.exitPresentation': string;
  'fileViewer.shareLabel': string;
  'fileViewer.shareMenuShareLink': string;
  'fileViewer.shareMenuPublishOnline': string;
  'fileViewer.shareMenuDownload': string;
  'fileViewer.shareMenuPresentation': string;
  'fileViewer.shareMenuSourceFiles': string;
  'fileViewer.shareMenuSave': string;
  'fileViewer.copyShareLink': string;
  'fileViewer.openSharePage': string;
  'fileViewer.shareLinkRequiresDeploy': string;
  'fileViewer.shareLinkPublishGuide': string;
  'fileViewer.shareAfterGenerationComplete': string;
  'fileViewer.copyProviderLink': string;
  'fileViewer.copyCloudflareLink': string;
  'fileViewer.screenshotCopying': string;
  'fileViewer.screenshotCopied': string;
  'fileViewer.screenshotClipboardDenied': string;
  'fileViewer.screenshotPreviewLoading': string;
  'fileViewer.screenshotCaptureFailed': string;
  'fileViewer.exportPdf': string;
  'fileViewer.exportPdfAllSlides': string;
  'fileViewer.exportPptxBusy': string;
  'fileViewer.exportPptxHint': string;
  'fileViewer.exportPptxNa': string;
  'fileViewer.exportZip': string;
  'fileViewer.exportHtml': string;
  'fileViewer.exportMd': string;
  'fileViewer.exportImage': string;
  'fileViewer.exportPptxImages': string;
  'fileViewer.exportPptxEditable': string;
  'fileViewer.exportingProgress': string;
  'fileViewer.exportSlideProgress': string;
  'fileViewer.exportingElapsed': string;
  'fileViewer.exportSlideEta': string;
  'fileViewer.exportFailed': string;
  'fileViewer.exportDone': string;
  'fileViewer.exportImageFailed': string;
  'fileViewer.exportImageModalSubtitle': string;
  'fileViewer.exportImageFormatLabel': string;
  'fileViewer.exportImageSaving': string;
  'fileViewer.exportImageSaved': string;
  'fileViewer.exportImageDownloadStarted': string;
  'fileViewer.exportImageDownloadDetails': string;
  'fileViewer.exportJsx': string;
  'fileViewer.exportReactHtml': string;
  'fileViewer.exportStarted': string;
  'fileViewer.saveAsTemplate': string;
  'fileViewer.savingTemplate': string;
  'fileViewer.savedTemplate': string;
  'fileViewer.savedTemplateFail': string;
  'fileViewer.templateNamePrompt': string;
  'fileViewer.templateNameDefault': string;
  'fileViewer.templateDescPrompt': string;
  'liveArtifact.refresh.button': string;
  'liveArtifact.refresh.buttonTitle': string;
  'liveArtifact.refresh.loadingTitle': string;
  'liveArtifact.refresh.noSourceTitle': string;
  'liveArtifact.refresh.running': string;
  'liveArtifact.refresh.runningMessage': string;
  'liveArtifact.refresh.runningAction': string;
  'liveArtifact.refresh.successOne': string;
  'liveArtifact.refresh.successMany': string;
  'liveArtifact.refresh.successAction': string;
  'liveArtifact.refresh.previousFailure': string;
  'liveArtifact.refresh.failureAction': string;
  'liveArtifact.refresh.networkFailure': string;
  'liveArtifact.refresh.genericFailure': string;
  'liveArtifact.refresh.statusNever': string;
  'liveArtifact.refresh.statusReady': string;
  'liveArtifact.refresh.statusSucceeded': string;
  'liveArtifact.refresh.statusFailed': string;
  'liveArtifact.refresh.statusRunning': string;
  'liveArtifact.refresh.statusRunningDescription': string;
  'liveArtifact.refresh.statusSucceededDescription': string;
  'liveArtifact.refresh.statusFailedDescription': string;
  'liveArtifact.refresh.statusReadyDescription': string;
  'liveArtifact.refresh.statusNeverDescription': string;
  'liveArtifact.refresh.eventStarted': string;
  'liveArtifact.refresh.eventSucceeded': string;
  'liveArtifact.refresh.eventFailed': string;
  'liveArtifact.refresh.eventStartedDetail': string;
  'liveArtifact.refresh.sourcesUpdatedOne': string;
  'liveArtifact.refresh.sourcesUpdatedMany': string;
  'liveArtifact.refresh.timelineEmpty': string;
  'liveArtifact.refresh.heroLastRefreshedLabel': string;
  'liveArtifact.refresh.heroLastRefreshedNever': string;
  'liveArtifact.refresh.justNow': string;
  'liveArtifact.refresh.factCreated': string;
  'liveArtifact.refresh.factLastUpdated': string;
  'liveArtifact.refresh.factUnknown': string;
  'liveArtifact.refresh.persistedTitle': string;
  'liveArtifact.refresh.persistedHint': string;
  'liveArtifact.refresh.persistedEmpty': string;
  'liveArtifact.refresh.persistedStatusSucceeded': string;
  'liveArtifact.refresh.persistedStatusRunning': string;
  'liveArtifact.refresh.persistedStatusFailed': string;
  'liveArtifact.refresh.persistedStatusCancelled': string;
  'liveArtifact.refresh.persistedStatusSkipped': string;
  'liveArtifact.refresh.sessionTitle': string;
  'liveArtifact.refresh.sessionHint': string;
  'liveArtifact.refresh.docSourceTitle': string;
  'liveArtifact.refresh.docSourceHint': string;
  'liveArtifact.refresh.docSourceType': string;
  'liveArtifact.refresh.docSourceTool': string;
  'liveArtifact.refresh.docSourceConnector': string;
  'liveArtifact.refresh.debugSummary': string;
  'liveArtifact.refresh.debugNote': string;
  'fileViewer.deployProviderLabel': string;
  'fileViewer.vercelProvider': string;
  'fileViewer.cloudflarePagesProvider': string;
  'fileViewer.deployToProvider': string;
  'fileViewer.redeployToProvider': string;
  'fileViewer.deployingToProvider': string;
  'liveArtifact.viewer.tabPreview': string;
  'liveArtifact.viewer.tabCode': string;
  'liveArtifact.viewer.tabData': string;
  'liveArtifact.viewer.tabRefreshHistory': string;
  'liveArtifact.viewer.dataEmpty': string;
  'liveArtifact.viewer.code.templateHeading': string;
  'liveArtifact.viewer.code.renderedHeading': string;
  'liveArtifact.viewer.code.templateHelp': string;
  'liveArtifact.viewer.code.renderedHelp': string;
  'liveArtifact.viewer.code.variantAria': string;
  'liveArtifact.viewer.code.variantTemplate': string;
  'liveArtifact.viewer.code.variantRendered': string;
  'liveArtifact.viewer.code.loading': string;
  'liveArtifact.viewer.code.unavailable': string;
  'liveArtifact.viewer.code.empty': string;
  'fileViewer.deployToVercel': string;
  'fileViewer.redeployToVercel': string;
  'fileViewer.deployingToVercel': string;
  'fileViewer.preparingPublicLink': string;
  'fileViewer.copyDeployLink': string;
  'fileViewer.deployModalTitle': string;
  'fileViewer.deployModalSubtitle': string;
  'fileViewer.vercelToken': string;
  'fileViewer.vercelTokenGetLink': string;
  'fileViewer.vercelTokenPlaceholder': string;
  'fileViewer.vercelTokenReuseHint': string;
  'fileViewer.vercelTokenRequired': string;
  'fileViewer.cloudflareApiToken': string;
  'fileViewer.cloudflareApiTokenGetLink': string;
  'fileViewer.cloudflareApiTokenPlaceholder': string;
  'fileViewer.cloudflareApiTokenReuseHint': string;
  'fileViewer.cloudflareApiTokenRequired': string;
  'fileViewer.cloudflareApiTokenScopeHint': string;
  'fileViewer.vercelTeamId': string;
  'fileViewer.vercelTeamSlug': string;
  'fileViewer.cloudflareAccountId': string;
  'fileViewer.cloudflareAccountIdHint': string;
  'fileViewer.cloudflareAccountIdRequired': string;
  'fileViewer.cloudflareZoneLabel': string;
  'fileViewer.cloudflareZonePlaceholder': string;
  'fileViewer.cloudflareZoneRequired': string;
  'fileViewer.cloudflareZonesLoading': string;
  'fileViewer.cloudflareZonesRefresh': string;
  'fileViewer.cloudflareZonesLoadFailed': string;
  'fileViewer.cloudflareZonesEmpty': string;
  'fileViewer.cloudflareDomainPrefixLabel': string;
  'fileViewer.cloudflareDomainPrefixPlaceholder': string;
  'fileViewer.cloudflareDomainPrefixInvalid': string;
  'fileViewer.cloudflareHostnamePreview': string;
  'fileViewer.cloudflareCustomDomainHint': string;
  'fileViewer.cloudflarePagesDevLinkLabel': string;
  'fileViewer.cloudflareCustomDomainLinkLabel': string;
  'fileViewer.optional': string;
  'fileViewer.vercelPreviewOnly': string;
  'fileViewer.cloudflarePagesPreviewHint': string;
  'fileViewer.savingConfig': string;
  'fileViewer.deployConfigSaveFailed': string;
  'fileViewer.deployFailed': string;
  'fileViewer.deployProviderConfigSaveFailed': string;
  'fileViewer.deployProviderFailed': string;
  'fileViewer.deployResultLabel': string;
  'fileViewer.deployLinkReady': string;
  'fileViewer.deploySuccessToast': string;
  'fileViewer.deploySuccessToastDetails': string;
  'fileViewer.deployLinkPreparingLabel': string;
  'fileViewer.deployLinkDelayed': string;
  'fileViewer.deployLinkFailed': string;
  'fileViewer.deployLinkProtectedLabel': string;
  'fileViewer.deployLinkProtected': string;
  'fileViewer.retryLink': string;

  // Question form (assistant question UX)
  'questionForm.submit': string;
  'questionForm.skip': string;
  'questionForm.locked': string;

  // Conversations dropdown
  'conv.switch': string;
  'conv.label': string;
  'conv.heading': string;
  'conv.new': string;
  'conv.empty': string;
  'conv.untitled': string;
  'conv.renameTooltip': string;
  'conv.delete': string;
  'conv.deleteConfirm': string;

  // Agent picker (legacy / alt)
  'agentPicker.label': string;
  'agentPicker.modeChoose': string;
  'agentPicker.localCli': string;
  'agentPicker.daemonOff': string;
  'agentPicker.byok': string;
  'agentPicker.selectAgent': string;
  'agentPicker.noAgents': string;
  'agentPicker.notInstalled': string;
  'agentPicker.rescan': string;

  // Tool cards (assistant action cards)
  'tool.openInTab': string;
  'tool.open': string;
  'tool.todos': string;
  'tool.todosExpand': string;
  'tool.todosCollapse': string;
  'tool.todosDone': string;
  'tool.todosDismiss': string;
  'tool.write': string;
  'tool.edit': string;
  'tool.read': string;
  'tool.bash': string;
  'tool.glob': string;
  'tool.grep': string;
  'tool.fetch': string;
  'tool.search': string;
  'tool.lines': string;
  'tool.changeSingular': string;
  'tool.changePlural': string;
  'tool.in': string;
  'tool.hide': string;
  'tool.output': string;
  'tool.running': string;
  'tool.error': string;
  'tool.done': string;

  // Assistant message scaffolding
  'assistant.role': string;
  'assistant.workingLabel': string;
  'assistant.doneLabel': string;
  'assistant.copyMarkdown': string;
  'assistant.forkConversation': string;
  'assistant.forkingConversation': string;
  'assistant.shareToOpenDesign': string;
  'assistant.shareToOpenDesignBusy': string;
  'assistant.feedbackPrompt': string;
  'assistant.feedbackPositive': string;
  'assistant.feedbackNegative': string;
  'assistant.feedbackReasonTitle': string;
  'assistant.feedbackReasonPositiveMatched': string;
  'assistant.feedbackReasonPositiveVisual': string;
  'assistant.feedbackReasonPositiveUseful': string;
  'assistant.feedbackReasonPositiveEasy': string;
  'assistant.feedbackReasonPositiveDesignSystem': string;
  'assistant.feedbackReasonNegativeMissed': string;
  'assistant.feedbackReasonNegativeVisual': string;
  'assistant.feedbackReasonNegativeIncomplete': string;
  'assistant.feedbackReasonNegativeHard': string;
  'assistant.feedbackReasonNegativeDesignSystem': string;
  'assistant.feedbackReasonOther': string;
  'assistant.feedbackReasonPlaceholder': string;
  'assistant.feedbackReasonSubmit': string;
  'assistant.emptyResponseLabel': string;
  'assistant.emptyResponseMessage': string;
  'assistant.unfinishedLabel': string;
  'assistant.unfinishedSummary': string;
  'assistant.unfinishedMore': string;
  'assistant.continueRemaining': string;
  'chat.resumeRunCta': string;
  'assistant.outTokens': string;
  'assistant.producedFiles': string;
  'assistant.openFile': string;
  'assistant.downloadFile': string;
  'nextStep.title': string;
  'nextStep.more': string;
  'nextStep.share': string;
  'nextStep.download': string;
  'nextStep.contribute': string;
  'nextStep.brandAiOptimizeTitle': string;
  'nextStep.brandAiOptimizeBody': string;
  'nextStep.brandCreateDesignTitle': string;
  'nextStep.brandCreateDesignBody': string;
  'nextStep.createDesignBusy': string;
  'assistant.thinking': string;
  'assistant.thought': string;
  'assistant.thoughtFor': string;
  'assistant.systemReminder': string;
  'assistant.possiblePromptInjection': string;
  'assistant.waitingFirstOutput': string;
  'assistant.statusBootingAgent': string;
  'assistant.statusPreparing': string;
  'assistant.statusStarting': string;
  'assistant.statusRequesting': string;
  'assistant.statusThinking': string;
  'assistant.statusStreaming': string;
  'assistant.slowHint': string;
  'assistant.verbEditing': string;
  'assistant.verbWriting': string;
  'assistant.verbReading': string;
  'assistant.verbSearching': string;
  'assistant.verbRunning': string;
  'assistant.verbTodos': string;
  'assistant.verbFetching': string;
  'assistant.verbCalling': string;

  // Question form labels
  'qf.answered': string;
  'qf.choose': string;
  'qf.required': string;
  'qf.lockedSubmitted': string;
  'qf.lockedPrev': string;
  'qf.hint': string;
  'qf.submitDefault': string;
  'qf.submitDisabledTitle': string;
  'qf.submitTitle': string;
  'qf.cardSelected': string;
  'qf.cardRefs': string;
  'qf.cardSampleText': string;
  'questions.tabLabel': string;
  'questions.banner': string;
  'questions.bannerAnswered': string;
  'questions.continue': string;
  'questions.generating': string;
  'questions.skipAll': string;
  'questions.autoSkipHint': string;

  // Inline <od-card> memory cards (display-only siblings of question-form)
  'artifact.odCardTaskBriefChip': string;
  'artifact.odCardScorecardTitle': string;
  'artifact.odCardScorecardStatusPass': string;
  'artifact.odCardScorecardStatusPartial': string;
  'artifact.odCardScorecardStatusFail': string;
  'artifact.odCardRuleKicker': string;
  'artifact.odCardRuleNameLabel': string;
  'artifact.odCardRuleDescriptionLabel': string;
  'artifact.odCardRuleAssertionLabel': string;
  'artifact.odCardRuleCheckLabel': string;
  'artifact.odCardRuleRationaleLabel': string;
  'artifact.odCardRuleKeep': string;
  'artifact.odCardRuleEdit': string;
  'artifact.odCardRuleDone': string;
  'artifact.odCardRuleDiscard': string;
  'artifact.odCardRuleSaving': string;
  'artifact.odCardRuleSaved': string;
  'artifact.odCardRuleError': string;
  'artifact.odCardBrandAssistDone': string;
  'artifact.odCardBrandAssistKicker': string;
  'artifact.odCardBrandAssistBody': string;
  'artifact.odCardBrandAssistError': string;
  'artifact.odCardBrandAssistWorking': string;
  'artifact.odCardBrandAssistConfirm': string;

  // Pet (Codex-style floating companion)
  'pet.title': string;
  'pet.subtitle': string;
  'pet.navTitle': string;
  'pet.navHint': string;
  // Tabs in pet settings — split sources so the choice feels exclusive
  'pet.tabBuiltIn': string;
  'pet.tabBuiltInHint': string;
  'pet.builtInEmpty': string;
  'pet.tabCustom': string;
  'pet.tabCustomHint': string;
  'pet.tabCommunity': string;
  'pet.tabCommunityHint': string;
  'pet.tabsAria': string;
  'pet.adopt': string;
  'pet.adoptedBadge': string;
  'pet.adoptCallout': string;
  'pet.changePet': string;
  'pet.wake': string;
  'pet.tuck': string;
  'pet.wakeTitle': string;
  'pet.tuckTitle': string;
  'pet.settingsTitle': string;
  'pet.useCustom': string;
  'pet.customTitle': string;
  'pet.customHint': string;
  'pet.customGreetingPlaceholder': string;
  'pet.fieldName': string;
  'pet.fieldGlyph': string;
  'pet.fieldGlyphHint': string;
  'pet.fieldGreeting': string;
  'pet.fieldAccent': string;
  'pet.fieldAccentCustom': string;
  'pet.fieldAccentDefault': string;
  'pet.overlayAria': string;
  'pet.spriteAria': string;
  'pet.spriteTitle': string;
  'pet.taskSummarySingle': string;
  'pet.taskSummaryMultiple': string;
  'pet.taskSummaryRecentSingle': string;
  'pet.taskSummaryRecentMultiple': string;
  'pet.taskListAria': string;
  'pet.taskOpenProject': string;
  'pet.taskGroup.running': string;
  'pet.taskGroup.queued': string;
  'pet.taskGroup.recent': string;
  'pet.idleQuote.leonardo.text': string;
  'pet.idleQuote.leonardo.author': string;
  'pet.idleQuote.michelangelo.text': string;
  'pet.idleQuote.michelangelo.author': string;
  'pet.idleQuote.bernini.text': string;
  'pet.idleQuote.bernini.author': string;
  'pet.idleQuote.raphael.text': string;
  'pet.idleQuote.raphael.author': string;
  'pet.idleQuote.caravaggio.text': string;
  'pet.idleQuote.caravaggio.author': string;
  'pet.idleQuote.rodin.text': string;
  'pet.idleQuote.rodin.author': string;
  // Composer pet menu
  'pet.composerTitle': string;
  'pet.composerMenuTitle': string;
  'pet.composerMenuHint': string;
  'pet.composerOpenSettings': string;
  // Welcome modal teaser
  'pet.welcomeTeaserTitle': string;
  'pet.welcomeTeaserBody': string;
  'pet.welcomeTeaserCta': string;
  // Image upload + spritesheet controls
  'pet.imageUpload': string;
  'pet.imageReplace': string;
  'pet.imageRemove': string;
  'pet.imageHintIdle': string;
  'pet.imageHintActive': string;
  'pet.fieldFrames': string;
  'pet.fieldFramesHint': string;
  'pet.fieldFps': string;
  'pet.fieldFpsHint': string;

  // Codex hatch-pet skill — atlas import + AI generation
  'pet.atlasImport': string;
  'pet.atlasImportTitle': string;
  'pet.atlasPickerTitle': string;
  'pet.atlasPickerHint': string;
  'pet.atlasCancel': string;
  'pet.atlasAdopt': string;
  'pet.atlasAdoptFull': string;
  'pet.atlasAdoptFullTitle': string;
  'pet.atlasAdoptRowTitle': string;
  'pet.atlasActiveHint': string;
  'pet.atlasRow.idle': string;
  'pet.atlasRow.running-right': string;
  'pet.atlasRow.running-left': string;
  'pet.atlasRow.waving': string;
  'pet.atlasRow.jumping': string;
  'pet.atlasRow.failed': string;
  'pet.atlasRow.waiting': string;
  'pet.atlasRow.running': string;
  'pet.atlasRow.review': string;
  'pet.hatchTitle': string;
  'pet.hatchHint': string;
  'pet.hatchConcept': string;
  'pet.hatchConceptPlaceholder': string;
  'pet.hatchCopy': string;
  'pet.hatchCopied': string;
  'pet.hatchFoot': string;
  // Slash-command popover in the chat composer
  'pet.slashPopoverAria': string;
  'pet.slashPopoverTitle': string;
  'pet.slashPopoverHint': string;
  'pet.slashPet': string;
  'pet.slashPetWake': string;
  'pet.slashPetTuck': string;
  'pet.slashHatch': string;
  'pet.slashHatchArg': string;
  'pet.slashSearch': string;
  'pet.slashSearchArg': string;
  // Recently-hatched section in pet settings
  'pet.codexTitle': string;
  'pet.codexSubtitle': string;
  'pet.codexSubtitleWithDir': string;
  'pet.codexEmpty': string;
  'pet.codexLoading': string;
  'pet.codexRefresh': string;
  'pet.codexAdopt': string;
  'pet.codexAdopting': string;
  'pet.communitySync': string;
  'pet.communitySyncing': string;
  'pet.communitySyncTitle': string;
  'pet.communitySyncDone': string;
  'pet.communitySyncFailed': string;
  'pet.codexBundled': string;
  'pet.codexBundledTitle': string;

  // Sketch editor
  'sketch.toolSelect': string;
  'sketch.toolPen': string;
  'sketch.toolText': string;
  'sketch.toolRect': string;
  'sketch.toolArrow': string;
  'sketch.toolEraser': string;
  'sketch.color': string;
  'sketch.strokeSize': string;
  'sketch.undo': string;
  'sketch.clear': string;
  'sketch.close': string;
  'sketch.closeConfirm': string;
  'sketch.textPrompt': string;
  'sketch.textModalTitle': string;
  // Critique Theater (Phase 8 components; Phase 9 fills non-English locales)
  'critiqueTheater.userFacingName': string;
  'critiqueTheater.roleDesigner': string;
  'critiqueTheater.roleCritic': string;
  'critiqueTheater.roleBrand': string;
  'critiqueTheater.roleA11y': string;
  'critiqueTheater.roleCopy': string;
  'critiqueTheater.roundLabel': string;
  'critiqueTheater.mustFix': string;
  'critiqueTheater.composite': string;
  'critiqueTheater.threshold': string;
  'critiqueTheater.consensus': string;
  'critiqueTheater.interrupt': string;
  'critiqueTheater.interrupting': string;
  'critiqueTheater.interrupted': string;
  'critiqueTheater.degradedHeading': string;
  'critiqueTheater.degradedReasonMalformed': string;
  'critiqueTheater.degradedReasonOversize': string;
  'critiqueTheater.degradedReasonAdapter': string;
  'critiqueTheater.degradedReasonProtocol': string;
  'critiqueTheater.degradedReasonMissingArtifact': string;
  'critiqueTheater.replay': string;
  'critiqueTheater.replaySpeed': string;
  'critiqueTheater.readOnly': string;
  'critiqueTheater.shippedSummary': string;
  'critiqueTheater.interruptedSummary': string;
  'critiqueTheater.shippedBadge': string;
  'critiqueTheater.belowThresholdBadge': string;
  'critiqueTheater.timedOutBadge': string;
  'critiqueTheater.failedHeading': string;
  'critiqueTheater.failedReasonCliExit': string;
  'critiqueTheater.failedReasonPerRoundTimeout': string;
  'critiqueTheater.failedReasonTotalTimeout': string;
  'critiqueTheater.failedReasonOrchestrator': string;
  'critiqueTheater.transcriptEmpty': string;
  'critiqueTheater.transcriptLoading': string;
  'critiqueTheater.transcriptError': string;
  'critiqueTheater.replaySpeedPaused': string;
  'critiqueTheater.replaySpeedInstant': string;
  'critiqueTheater.replaySpeedLive': string;
  'critiqueTheater.replaySpeedFast': string;
  'critiqueTheater.settingsNav': string;
  'critiqueTheater.settingsNavHint': string;
  'critiqueTheater.settingsEnabledLabel': string;
  'critiqueTheater.settingsEnabledDescription': string;
  'critiqueTheater.settingsEnabledProjectHint': string;
  'critiqueTheater.settingsEnabledNoProjectHint': string;

  // Diagnostics export
  'diagnostics.exportTitle': string;
  'diagnostics.exportButton': string;
  'diagnostics.exportHint': string;
  'diagnostics.exporting': string;
  'diagnostics.exportSuccess': string;
  'diagnostics.exportFailed': string;

  // Brands library + extraction
  'brand.libraryTitle': string;
  'brand.librarySubtitle': string;
  'brand.newBrand': string;
  'brand.searchPlaceholder': string;
  'brand.empty': string;
  'brand.colorsCount': string;
  'brand.extracting': string;
  'brand.failed': string;
  'brand.needsInput': string;
  'brand.needsInputHint': string;
  'brand.appliedToChat': string;
  'brand.previewEmpty': string;
  'brand.viewDetails': string;
  'newBrand.title': string;
  'newBrand.subtitle': string;
  'newBrand.urlLabel': string;
  'newBrand.urlPlaceholder': string;
  'newBrand.extract': string;
  'newBrand.cancel': string;
  'newBrand.close': string;
  'newBrand.stage1': string;
  'newBrand.stage2': string;
  'newBrand.stage3': string;
  'newBrand.measured': string;
  'newBrand.done': string;
  'brandDetail.identity': string;
  'brandDetail.designLanguage': string;
  'brandDetail.colors': string;
  'brandDetail.fonts': string;
  'brandDetail.tone': string;
  'brandDetail.aesthetic': string;
  'brandDetail.tagline': string;
  'brandDetail.voice': string;
  'brandDetail.useInChat': string;
  'brandDetail.delete': string;
  'brandDetail.back': string;
  'brandDetail.refining': string;
  'brandDetail.notFound': string;
  'brandDetail.logo': string;
  'brandDetail.typography': string;
  'brandDetail.palette': string;
  'brandDetail.voiceTone': string;
  'brandDetail.imageryLayout': string;
  'brandDetail.images': string;
  'brandDetail.designSystem': string;
  'brandDetail.brandAssets': string;
  'brandDetail.openProject': string;
  'brandDetail.themeLight': string;
  'brandDetail.themeDark': string;
  'brandDetail.subjects': string;
  'brandDetail.treatment': string;
  'brandDetail.useLabel': string;
  'brandDetail.avoidLabel': string;
  'brandDetail.layoutPosture': string;
  'brandDetail.deleteConfirm': string;
  'brandDetail.viewMore': string;
  'brandDetail.viewLess': string;
  'settings.resetOnboarding': string;
  'settings.resetOnboardingDesc': string;
  'settings.resetOnboardingButton': string;
  'onboarding.brandTitle': string;
  'onboarding.brandSubtitle': string;
  'onboarding.brandSkip': string;
  'onboarding.brandDone': string;
  // Final onboarding step — invite the user to build with brands + design systems
  'onboarding.buildTitle': string;
  'onboarding.buildBody': string;
  'onboarding.buildBenefitMemoryTitle': string;
  'onboarding.buildBenefitMemoryBody': string;
  'onboarding.buildBenefitAlignedTitle': string;
  'onboarding.buildBenefitAlignedBody': string;
  'onboarding.buildBenefitSourcesTitle': string;
  'onboarding.buildBenefitSourcesBody': string;
  'onboarding.buildPreviewLabel': string;
  'onboarding.buildStart': string;
  'onboarding.buildHome': string;
  'dsCreate.referenceLabel': string;
  'dsCreate.referenceLoading': string;
  'dsCreate.referenceLoadFailed': string;
  'dsCreate.heroShowGuideAria': string;
  'dsCreate.heroHideGuideAria': string;
  'dsCreate.heroShowGuideTitle': string;
  'dsCreate.heroHideGuideTitle': string;
  'dsCreate.heroGuide': string;
  'dsCreate.missingSourceError': string;
  'dsCreate.continueToGeneration': string;
  'dsCreate.embeddedTitle': string;
  'dsCreate.embeddedBody': string;
  'dsCreate.sourceSectionTitle': string;
  'dsCreate.sourceSectionBody': string;
  'dsCreate.githubWebsiteLabel': string;
  'dsCreate.add': string;
  'dsCreate.startFromBrand': string;
  'dsCreate.brandPickerSubtitle': string;
  'dsCreate.brandPickerQuickPicks': string;
  'dsCreate.addedSourceLinks': string;
  'dsCreate.openSourceLabel': string;
  'dsCreate.removeSourceLabel': string;
  'dsCreate.addFiles': string;
  'dsCreate.describeBrand': string;
  'dsCreate.optional': string;
  'dsCreate.describeBrandHelp': string;
  'dsCreate.companyPlaceholder': string;
  'dsCreate.pasteDesignMd': string;
  'dsCreate.pasteDesignMdHelp': string;
  'dsCreate.reference': string;
  'dsCreate.designMdViewMode': string;
  'dsCreate.advancedToggle': string;
  'dsCreate.githubRepo': string;
  'dsCreate.localCodeLabel': string;
  'dsCreate.localCodeHelper': string;
  'dsCreate.localCodePrompt': string;
  'dsCreate.uploadFigLabel': string;
  'dsCreate.uploadFigHelper': string;
  'dsCreate.uploadFigPrompt': string;
  'dsCreate.figmaUrl': string;
  'dsCreate.figmaPlaceholder': string;
  'dsCreate.addedFigmaUrls': string;
  'dsCreate.savedFigmaHelp': string;
  'dsCreate.notes': string;
  'dsCreate.notesPlaceholder': string;
  'dsCreate.back': string;
  'dsCreate.generate': string;
  'dsCreate.libraryPickerTitle': string;
  'dsCreate.libraryPickerConfirm': string;
  'dsCreate.designMdPreviewKicker': string;
  'dsCreate.designMdPreviewTitle': string;
  'dsCreate.componentKit': string;
  'dsCreate.defaultTheme': string;
  'dsCreate.componentKitTitle': string;
  'dsCreate.designMdGeneratedDescription': string;
  'dsCreate.previewButtons': string;
  'dsCreate.previewButtonsHelp': string;
  'dsCreate.buttonPrimary': string;
  'dsCreate.buttonDashed': string;
  'dsCreate.buttonText': string;
  'dsCreate.buttonLink': string;
  'dsCreate.sizeSmall': string;
  'dsCreate.sizeMedium': string;
  'dsCreate.sizeLarge': string;
  'dsCreate.previewTypeScale': string;
  'dsCreate.extractedTokens': string;
  // Brand reference picker — pick a brand to extract from
  'brandPicker.heading': string;
  'brandPicker.subtext': string;
  'brandPicker.quickPicksLabel': string;
  'brandPicker.searchPlaceholder': string;
  'brandPicker.allCategories': string;
  'brandPicker.extractAction': string;
  'brandPicker.showMore': string;
  'brandPicker.opening': string;
}
